import React, { useState, useRef, useEffect } from 'react';
import {
    Camera, Copy, Check, Coffee, Sparkles, Share2, Trash2, Flame, IceCream,
    Info, ExternalLink, Ruler, Package, Scale, Crown, Gift, Edit3, X, AlertCircle
} from 'lucide-react';
import { toPng } from 'html-to-image';
import Confetti from 'react-confetti';

// =============================================================================
// CONFIGURATION
// =============================================================================

const RARITY_TIERS = {
    'Common': { color: 'border-gray-400', bg: 'bg-gray-50', text: 'text-gray-600', emoji: '🗑️' },
    'Uncommon': { color: 'border-green-400', bg: 'bg-green-50', text: 'text-green-600', emoji: '🍀' },
    'Rare': { color: 'border-blue-400', bg: 'bg-blue-50', text: 'text-blue-600', emoji: '💎' },
    'Epic': { color: 'border-purple-500', bg: 'bg-purple-50', text: 'text-purple-600', emoji: '🔮' },
    'Legendary': { color: 'border-yellow-500', bg: 'bg-yellow-50', text: 'text-yellow-600', emoji: '👑' },
    'God-Tier': { color: 'border-rose-500', bg: 'bg-rose-50', text: 'text-rose-600', emoji: '🔥' }
};

const GLOBAL_REGIONS = {
    'US': { currency: 'USD', symbol: '$' },
    'UK': { currency: 'GBP', symbol: '£' },
    'DE': { currency: 'EUR', symbol: '€' },
    'AU': { currency: 'AUD', symbol: 'A$' },
    'CA': { currency: 'CAD', symbol: 'C$' },
    'EU': { currency: 'EUR', symbol: '€' }
};

const SPICYLISTER_PACKAGING = {
    'large-letter': {
        dimensions: '24×16×3cm',
        maxDimensions: { length: 24, width: 16, height: 3 },
        maxWeight: 750,
        price: 0.85,
        name: 'SpicyLister Large Letter',
        icon: '📬',
        description: 'Perfect for flat items'
    },
    'small-parcel': {
        dimensions: '45×35×16cm',
        maxDimensions: { length: 45, width: 35, height: 16 },
        maxWeight: 2000,
        price: 1.20,
        name: 'SpicyLister Small Box',
        icon: '📦',
        description: 'Most popular size'
    },
    'medium-parcel': {
        dimensions: '61×46×46cm',
        maxDimensions: { length: 61, width: 46, height: 46 },
        maxWeight: 20000,
        price: 2.50,
        name: 'SpicyLister Medium Box',
        icon: '📦',
        description: 'For larger items'
    },
    'large-parcel': {
        dimensions: 'Custom',
        maxDimensions: { length: 999, width: 999, height: 999 },
        maxWeight: 30000,
        price: 4.00,
        name: 'SpicyLister Large Box',
        icon: '📦',
        description: 'Heavy duty protection'
    }
};

const SAVINGS_PER_LISTING = {
    time: 18,
    cost: 3.50
};

const WAITING_MESSAGES = [
    { type: 'joke', text: "Why did the eBay seller go to therapy? Too many unresolved listings." },
    { type: 'joke', text: "What do you call a fake noodle? An impasta. (Like fake designer gear on eBay)" },
    { type: 'joke', text: "I told my wife I was going to sell everything... She said 'List me first, I'm vintage.'" },
    { type: 'joke', text: "Why don't scientists trust atoms? Because they make up everything. Like 'mint condition' sellers." },
    { type: 'joke', text: "What's the difference between a photo of your item and your ex? Your item actually has value." },
    { type: 'joke', text: "I've got a great joke about postage... but it'll cost you £3.99 delivery." },
    { type: 'joke', text: "My decluttering is going great. I've sold 3 things and bought 7 on eBay." },
    { type: 'joke', text: "What did one listing say to the other? 'You're a steal at that price!'" },
    { type: 'fact', text: "The most expensive item ever sold on eBay was a yacht for $168 million in 2006." },
    { type: 'fact', text: "eBay was originally called AuctionWeb and the first item sold was a broken laser pointer for $14.83." },
    { type: 'fact', text: "There are approximately 1.7 billion listings on eBay at any given time." },
    { type: 'fact', text: "The word 'vintage' technically means between 20-100 years old. Everything else is just 'used'." },
    { type: 'fact', text: "Royal Mail delivers to 31 million addresses in the UK. Yours is about to get busier." },
    { type: 'fact', text: "The average UK household has over £1,000 worth of unused items. You're sitting on gold." },
    { type: 'fact', text: "eBay's 'Buy It Now' feature wasn't added until 2000 - three years after launch." },
    { type: 'fact', text: "The most watched eBay category in the UK? Electronics. The most fun? Collectibles." },
    { type: 'fact', text: "A 2023 study found decluttering reduces cortisol (stress hormone) by up to 25%." },
    { type: 'fact', text: "In Japan, there's a word 'tsundoku' - buying books and letting them pile up unread. Sound familiar?" },
    { type: 'tip', text: "Top tip: End your auctions on Sunday evenings - that's when most eBay buyers are browsing." },
    { type: 'tip', text: "Top tip: Include measurements in your listings. Returns drop by 50% when buyers know the size." },
    { type: 'tip', text: "Top tip: Clean your items before photographing. A quick wipe adds perceived value." },
    { type: 'tip', text: "Top tip: Natural daylight makes the best product photos. No flash needed." },
    { type: 'tip', text: "Top tip: Start auctions at 99p for maximum watchers. Trust the market." },
    { type: 'tip', text: "Top tip: Bundle similar items together. '5 books for £12' sells faster than 5 separate listings." },
    { type: 'tip', text: "Top tip: Mention brand names in your title. Buyers search for brands, not descriptions." },
    { type: 'tip', text: "Top tip: Ship within 24 hours for top-rated seller status. Speed = good reviews." },
    { type: 'tip', text: "Top tip: Reuse packaging from your own deliveries. Free, eco-friendly, and it works." },
    { type: 'tip', text: "Top tip: Take photos on a white background. Even a bedsheet works." },
    { type: 'hype', text: "You're literally making money while sitting down. This is the dream." },
    { type: 'hype', text: "Every item you list is space reclaimed and money earned. Double win." },
    { type: 'hype', text: "Somewhere out there, someone is searching for exactly what you're selling right now." },
    { type: 'hype', text: "You've got this. The hardest part was taking the photos. AI's got the rest." },
    { type: 'hype', text: "Fun fact: You're faster than 99% of eBay sellers right now. They're still writing titles." },
    { type: 'hype', text: "Your clutter is someone else's treasure. And you're about to find out how much treasure." },
    { type: 'hype', text: "Remember: done is better than perfect. A listed item sells. An unlisted one doesn't." },
    { type: 'hype', text: "This is the bit where normal people give up. But you? You've got SpicyLister." },
];

const getWaitingMessage = () => {
    return WAITING_MESSAGES[Math.floor(Math.random() * WAITING_MESSAGES.length)];
};

const MESSAGE_TYPE_EMOJI = {
    joke: '😂',
    fact: '🧠',
    tip: '💡',
    hype: '🔥'
};

const REWARDS = [
    '☕ A nice brew and biscuit',
    '🚶 A 20-minute walk in fresh air',
    '📖 A chapter of your book',
    '🎮 A quick gaming session',
    '🛀 A relaxing bath',
    '📱 Catch up on TikTok',
    '🎵 Listen to your favorite album',
    '🌳 Sit in nature for 15 minutes',
    '🧘 A short meditation break',
    '🎨 Doodle or color for fun',
    '☀️ Ten minutes in the sunshine',
    '💬 Text a friend you\'ve been meaning to reach',
    '🐕 Extra playtime with your pet',
    '🍫 Treat yourself to something nice'
];

const KUDOS_ACTIONS = {
    listing_created: { kudos: 5, coins: 2 },
    item_sold: { kudos: 10, coins: 5 },
    helped_user: { kudos: 15, coins: 5 },
    shared_tip: { kudos: 5, coins: 0 }
};

const TIER_LIMITS = {
    free: {
        listingsPerMonth: 5,
        features: ['Single photo', 'Basic AI', 'Packaging recommendations']
    },
    pro: {
        listingsPerMonth: 200,
        features: [
            'Video assessment',
            'Batch processing (10+ items)',
            'Advanced material analysis',
            'Market trend insights',
            'Priority support'
        ]
    }
};

const PATREON_OAUTH_URL = '/.netlify/functions/patreon-initiate';
// Stripe membership — Phase 1 of the Patreon->Stripe migration, proven
// end-to-end 2026-07-28/29. Runs alongside Patreon, doesn't replace it yet.
// VILLAGER99 auto-applies via the URL param — 99p first month, then £4.95/mo.
const STRIPE_VILLAGER_LINK = 'https://buy.stripe.com/bJe14ndAT9s21fzdu7frW09?prefilled_promo_code=VILLAGER99';
const STRIPE_FOUNDER_LINK = 'https://buy.stripe.com/bJe4gzbsLgUu3nHfCffrW08';
// Honesty box — one-off "thanks", no account needed. Patreon is for people who want
// to become an ongoing Fan/member instead — see the line below this link.
const HONESTY_BOX_URL = 'https://buymeacoffee.com/chrispteemagician';

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

const detectUserRegion = () => {
    try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (timezone.includes('Europe/London')) return 'UK';
        if (timezone.includes('America')) return 'US';
        if (timezone.includes('Australia')) return 'AU';
        return 'UK';
    } catch { return 'UK'; }
};

const detectUserLanguage = () => {
    try {
        const saved = localStorage.getItem('spicylister_lang');
        if (saved) return saved;
        const lang = (navigator.language || 'en').toLowerCase().slice(0, 2);
        const supported = ['en', 'de', 'es', 'fr', 'pl', 'it'];
        return supported.includes(lang) ? lang : 'en';
    } catch { return 'en'; }
};

const TRANSLATIONS = {
    en: {
        headline: 'Sell your clutter without a stutter',
        tagline: 'We write your title & description. You confirm the price and sort the postage.',
        snapTitle: '📸 Snap Something to Sell!',
        snapSub: "Photo an item you want to flog and I'll write the listing for you",
        camera: 'Camera', gallery: 'Gallery',
        analyzing: 'Analyzing...', cookingMagic: 'Cooking up magic...',
        generateListing: 'Generate Listing', generateSpicyListing: 'Generate Spicy Listing',
        vanilla: 'Vanilla', spicy: 'SPICY',
        spicyOn: '🔥 Neurospicy Mode: Engaged', vanillaOn: '🍦 Professional Mode: Clean & Simple',
        totalEstValue: 'Total estimated value',
        extraContext: 'Extra context (optional)',
        extraContextPlaceholder: "e.g. These are all books, from a smoke-free home, bought in 2020...",
        extraContextHelp: 'This info helps the AI write better descriptions for all items',
        generateN: (n) => `Generate ${n} Listing${n > 1 ? 's' : ''}`,
        generateNSpicy: (n) => `Generate ${n} Spicy Listing${n > 1 ? 's' : ''}`,
        exportCSVn: (n) => `Export eBay CSV (${n} items)`,
    },
    de: {
        tagline: 'Entrümple ohne Grübeln',
        snapTitle: '📸 Foto eines Artikels aufnehmen!',
        snapSub: 'Fotografiere einen Artikel und ich schreibe die Anzeige für dich',
        camera: 'Kamera', gallery: 'Galerie',
        analyzing: 'Analysiere...', cookingMagic: 'Magie am Kochen...',
        generateListing: 'Anzeige erstellen', generateSpicyListing: 'Scharf inserieren',
        vanilla: 'Vanille', spicy: 'SCHARF',
        spicyOn: '🔥 Neurospicy-Modus: Aktiv', vanillaOn: '🍦 Profimodus: Klar & Einfach',
        totalEstValue: 'Geschätzter Gesamtwert',
        extraContext: 'Zusatzinfos (optional)',
        extraContextPlaceholder: 'z.B. Das sind alles Bücher, aus einem Nichtraucherhaushalt...',
        extraContextHelp: 'Diese Infos helfen der KI, bessere Beschreibungen zu schreiben',
        generateN: (n) => `${n} Anzeige${n > 1 ? 'n' : ''} erstellen`,
        generateNSpicy: (n) => `${n} Scharfe Anzeige${n > 1 ? 'n' : ''} erstellen`,
        exportCSVn: (n) => `eBay CSV exportieren (${n} Artikel)`,
    },
    es: {
        tagline: 'Vende sin enredar la mente',
        snapTitle: '📸 ¡Fotografía algo para vender!',
        snapSub: 'Fotografía un artículo y yo escribiré el anuncio por ti',
        camera: 'Cámara', gallery: 'Galería',
        analyzing: 'Analizando...', cookingMagic: 'Cocinando magia...',
        generateListing: 'Generar anuncio', generateSpicyListing: 'Anuncio picante',
        vanilla: 'Vainilla', spicy: 'PICANTE',
        spicyOn: '🔥 Modo Neurospicy: Activado', vanillaOn: '🍦 Modo Profesional: Limpio y Simple',
        totalEstValue: 'Valor total estimado',
        extraContext: 'Contexto adicional (opcional)',
        extraContextPlaceholder: 'p.ej. Son todos libros, de un hogar sin humo, comprados en 2020...',
        extraContextHelp: 'Esta info ayuda a la IA a escribir mejores descripciones',
        generateN: (n) => `Generar ${n} anuncio${n > 1 ? 's' : ''}`,
        generateNSpicy: (n) => `${n} anuncio${n > 1 ? 's' : ''} picante${n > 1 ? 's' : ''}`,
        exportCSVn: (n) => `Exportar CSV eBay (${n} artículos)`,
    },
    fr: {
        tagline: 'Vendez sans vous prendre la tête',
        snapTitle: '📸 Prenez une photo à vendre !',
        snapSub: "Photographiez un article et je rédigerai l'annonce pour vous",
        camera: 'Caméra', gallery: 'Galerie',
        analyzing: 'Analyse en cours...', cookingMagic: 'Magie en préparation...',
        generateListing: "Générer l'annonce", generateSpicyListing: 'Annonce épicée',
        vanilla: 'Vanille', spicy: 'ÉPICÉ',
        spicyOn: '🔥 Mode Neurospicy : Activé', vanillaOn: '🍦 Mode Professionnel : Simple et Net',
        totalEstValue: 'Valeur totale estimée',
        extraContext: 'Contexte supplémentaire (optionnel)',
        extraContextPlaceholder: "p.ex. Ce sont tous des livres, d'un foyer non-fumeur...",
        extraContextHelp: "Ces infos aident l'IA à rédiger de meilleures descriptions",
        generateN: (n) => `Générer ${n} annonce${n > 1 ? 's' : ''}`,
        generateNSpicy: (n) => `${n} annonce${n > 1 ? 's' : ''} épicée${n > 1 ? 's' : ''}`,
        exportCSVn: (n) => `Exporter CSV eBay (${n} articles)`,
    },
    pl: {
        tagline: 'Sprzedaj bez bólu głowy',
        snapTitle: '📸 Zrób zdjęcie czegoś do sprzedania!',
        snapSub: 'Sfotografuj przedmiot, a ja napiszę ogłoszenie za ciebie',
        camera: 'Aparat', gallery: 'Galeria',
        analyzing: 'Analizuję...', cookingMagic: 'Tworzę magię...',
        generateListing: 'Utwórz ogłoszenie', generateSpicyListing: 'Ostre ogłoszenie',
        vanilla: 'Wanilia', spicy: 'OSTRE',
        spicyOn: '🔥 Tryb Neurospicy: Aktywny', vanillaOn: '🍦 Tryb Profesjonalny: Czysty i Prosty',
        totalEstValue: 'Szacowana łączna wartość',
        extraContext: 'Dodatkowy kontekst (opcjonalnie)',
        extraContextPlaceholder: 'np. To wszystko książki, z domu niepalącego, kupione w 2020...',
        extraContextHelp: 'Te informacje pomagają AI pisać lepsze opisy',
        generateN: (n) => `Utwórz ${n} ogłosze${n === 1 ? 'nie' : 'ń'}`,
        generateNSpicy: (n) => `${n} ostre ogłosze${n === 1 ? 'nie' : 'ń'}`,
        exportCSVn: (n) => `Eksportuj CSV eBay (${n} przedmiotów)`,
    },
    it: {
        tagline: 'Vendi senza pensarci troppo',
        snapTitle: '📸 Fotografa qualcosa da vendere!',
        snapSub: "Scatta una foto a un oggetto e scriverò l'annuncio per te",
        camera: 'Fotocamera', gallery: 'Galleria',
        analyzing: 'Analisi in corso...', cookingMagic: 'Preparando la magia...',
        generateListing: 'Crea annuncio', generateSpicyListing: 'Annuncio piccante',
        vanilla: 'Vaniglia', spicy: 'PICCANTE',
        spicyOn: '🔥 Modalità Neurospicy: Attiva', vanillaOn: '🍦 Modalità Professionale: Pulita e Semplice',
        totalEstValue: 'Valore totale stimato',
        extraContext: 'Contesto aggiuntivo (opzionale)',
        extraContextPlaceholder: 'es. Sono tutti libri, da una casa senza fumo, comprati nel 2020...',
        extraContextHelp: "Queste info aiutano l'IA a scrivere descrizioni migliori",
        generateN: (n) => `Crea ${n} annuncio${n > 1 ? 'i' : ''}`,
        generateNSpicy: (n) => `${n} annuncio${n > 1 ? 'i' : ''} piccant${n > 1 ? 'i' : 'e'}`,
        exportCSVn: (n) => `Esporta CSV eBay (${n} articoli)`,
    },
};

const compressImage = (file) => {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const MAX_WIDTH = 800;
                const MAX_HEIGHT = 800;
                let width = img.width;
                let height = img.height;
                if (width > height) {
                    if (width > MAX_WIDTH) { height *= MAX_WIDTH / width; width = MAX_WIDTH; }
                } else {
                    if (height > MAX_HEIGHT) { width *= MAX_HEIGHT / height; height = MAX_HEIGHT; }
                }
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);
                resolve(canvas.toDataURL('image/jpeg', 0.6));
            };
            img.src = e.target.result;
        };
        reader.readAsDataURL(file);
    });
};

const CATEGORY_MAP = {
    'Electronics': 'Electronics',
    'Electronics > Phones': 'Electronics',
    'Fashion': 'Fashion',
    'Fashion > Clothing': 'Fashion',
    'Home': 'Home & Garden',
    'Books': 'Books, Comics & Magazines',
    'Collectibles': 'Collectibles',
    'Collectables': 'Collectibles',
    'Art': 'Art',
    'Toys': 'Toys & Games',
    'Sports': 'Sports & Outdoors',
    'Antiques': 'Antiques'
};

const mapCategory = (category) => {
    if (!category) return 'Other';
    if (CATEGORY_MAP[category]) return CATEGORY_MAP[category];
    const mainCategory = category.split('>')[0].trim();
    if (CATEGORY_MAP[mainCategory]) return CATEGORY_MAP[mainCategory];
    const lowerCat = category.toLowerCase();
    if (lowerCat.includes('electronic') || lowerCat.includes('phone') || lowerCat.includes('computer')) return 'Electronics';
    if (lowerCat.includes('fashion') || lowerCat.includes('cloth') || lowerCat.includes('shoe')) return 'Fashion';
    if (lowerCat.includes('book') || lowerCat.includes('comic') || lowerCat.includes('magazine')) return 'Books, Comics & Magazines';
    if (lowerCat.includes('collect')) return 'Collectibles';
    if (lowerCat.includes('toy') || lowerCat.includes('game')) return 'Toys & Games';
    if (lowerCat.includes('home') || lowerCat.includes('garden') || lowerCat.includes('kitchen')) return 'Home & Garden';
    return 'Other';
};

// URLs sourced from feelfamous.co.uk Village Grid source — 2026-04-24
const OID_NETWORK = [
    { name: 'Motor-Oid', emoji: '🔧', url: 'https://motor-oid.co.uk', tagline: 'AI vehicle parts identifier', keywords: ['car','vehicle','auto','motor','engine','exhaust','bumper','wheel','tyre','tire','brake','gearbox','clutch','radiator','suspension','van','truck','lorry','motorcycle','motorbike','moped','scooter','carburettor','carburetor','alternator','headlight','bonnet','dashboard','steering','cambelt','camshaft','piston','manifold','diff','differential'] },
    { name: 'Watch-Oid', emoji: '⌚', url: 'https://watch-oid.co.uk', tagline: 'AI watch identifier', keywords: ['watch','timepiece','chronograph','rolex','seiko','omega','casio','citizen','timex','wristwatch','pocket watch','movement','dial','bezel','bracelet','strap','clasp','automatic','quartz','mechanical','horology'] },
    { name: 'Sail-Oid', emoji: '⛵', url: 'https://sail-oid.co.uk', tagline: 'AI boat & marine gear identifier', keywords: ['boat','sail','marine','nautical','anchor','rope','ship','vessel','yacht','canoe','kayak','outboard','tiller','rudder','mast','boom','winch','cleat','fender','buoy','life jacket','bilge','propeller','marina','dinghy'] },
    { name: 'Radi-Oid', emoji: '📻', url: 'https://radi-oid.co.uk', tagline: 'AI vintage radio & hi-fi identifier', keywords: ['radio','hifi','hi-fi','hi fi','amplifier','valve','tube','transistor','receiver','tuner','speaker','turntable','cassette','reel to reel','tape deck','stereo','audiophile','gramophone','phonograph','vhf','am fm','cb radio','ham radio'] },
    { name: 'Vinyl-Oid', emoji: '🎵', url: 'https://vinyl-oid.co.uk', tagline: 'AI vinyl record identifier', keywords: ['vinyl','record','lp','ep','7"','12"','7 inch','12 inch','pressing','first press','gatefold','rpm','45rpm','33rpm','shellac','78','sleeve','picture disc'] },
    { name: 'Guit-Oid', emoji: '🎸', url: 'https://guitar-oid.netlify.app', tagline: 'AI guitar & instrument identifier', keywords: ['guitar','bass guitar','ukulele','mandolin','banjo','fender','gibson','stratocaster','telecaster','les paul','acoustic','electric guitar','effects pedal','pickup','humbucker','single coil','amplifier','amp'] },
    { name: 'Coin-Oid', emoji: '🪙', url: 'https://coin-oid.netlify.app', tagline: 'AI coin & medal identifier', keywords: ['coin','medal','numismatic','bullion','sovereign','crown','penny','shilling','florin','farthing','sixpence','halfpenny','gold coin','silver coin','proof coin','uncirculated','mint condition','obverse','reverse'] },
    { name: 'Stamp-Oid', emoji: '✉️', url: 'https://stamp-oid.netlify.app', tagline: 'AI stamp identifier', keywords: ['stamp','postage stamp','philately','philatelic','overprint','perforated','imperforate','first day cover','royal mail','postal','franked','mint stamp','used stamp'] },
    { name: 'Magic-Oid', emoji: '🎩', url: 'https://magic-oid.co.uk', tagline: 'AI magic trick identifier', keywords: ['magic','magic trick','illusion','wand','card trick','conjuring','magician','mentalism','mentalist','prediction','vanish','production','transformation','close-up magic'] },
    { name: 'Miniature-Oid', emoji: '🏠', url: 'https://miniature-oid.netlify.app', tagline: 'AI miniature & dollhouse identifier', keywords: ['miniature','dollhouse','doll house','dolls house','figurine','warhammer','model','diorama','toy soldier','pewter','resin','40k','age of sigmar','dungeons and dragons','d&d','rpg','citadel','painted miniature','scale model'] },
    { name: 'Designer-Oid', emoji: '👗', url: 'https://designer-oid.co.uk', tagline: 'AI designer fashion identifier', keywords: ['designer','luxury','handbag','gucci','louis vuitton','lv','prada','chanel','hermes','dior','versace','burberry','coach','mulberry','branded','authentic','genuine leather','dust bag'] },
    { name: 'Camera-Oid', emoji: '📷', url: 'https://camera-oid.co.uk', tagline: 'AI camera & lens identifier', keywords: ['camera','lens','photography','film camera','dslr','mirrorless','nikon','canon','sony','fuji','fujifilm','leica','olympus','pentax','minolta','rangefinder','slr','medium format','darkroom','enlarger'] },
    { name: 'Travel-Oid', emoji: '✈️', url: 'https://travel-oid.co.uk', tagline: 'AI travel gear identifier', keywords: ['luggage','suitcase','backpack','travel bag','duffel','holdall','carry on','trolley','samsonite','rimowa','american tourister','delsey','antler','packing cube'] },
    { name: 'Fish-Oid', emoji: '🐟', url: 'https://fish-oid.netlify.app', tagline: 'AI fish & tackle identifier', keywords: ['fish','fishing','tackle','rod','reel','lure','fly fishing','bait','taxidermy fish','stuffed fish','fishing equipment','spinning rod','sea fishing','coarse fishing','feeder'] },
    { name: 'Cannabin-Oid', emoji: '🌿', url: 'https://cannabin-oid.co.uk', tagline: 'AI cannabis strain identifier', keywords: ['cannabis','strain','terpene','cbd','thc','indica','sativa','hybrid','bud','flower','hemp','hash','resin','trichome'] },
    { name: 'Retro-Oid', emoji: '🎮', url: 'https://retro-oid.netlify.app', tagline: 'Xbox backwards compat checker + gaming chatbot', keywords: ['game','gaming','console','xbox','playstation','ps5','ps4','ps3','nintendo','switch','retro game','board game','steam','pc game','video game','atari','sega','game boy','gamecube','wii','vita','backwards compatible','backward','compat','cex','game trade','cartridge','disc','ps1','n64','dreamcast','megadrive'] },
];

const getOidRecommendation = (results) => {
    if (!results) return null;
    const haystack = `${results.title} ${results.category || ''} ${results.description || ''}`.toLowerCase();
    let best = null;
    let bestScore = 0;
    for (const oid of OID_NETWORK) {
        let score = 0;
        for (const kw of oid.keywords) {
            if (haystack.includes(kw)) {
                score += results.title.toLowerCase().includes(kw) ? 3 : 1;
            }
        }
        if (score > bestScore) { bestScore = score; best = oid; }
    }
    return bestScore >= 2 ? best : null;
};

const CONDITION_MAP = {
    'New with tags': 'new',
    'New': 'new',
    'Like new': 'like-new',
    'Excellent condition': 'excellent',
    'Excellent': 'excellent',
    'Very good condition': 'excellent',
    'Good condition': 'good',
    'Good': 'good',
    'Fair condition': 'fair',
    'Fair': 'fair',
    'Used': 'good'
};

const mapCondition = (condition) => {
    if (!condition) return 'good';
    if (CONDITION_MAP[condition]) return CONDITION_MAP[condition];
    const lowerCond = condition.toLowerCase();
    if (lowerCond.includes('new')) return 'new';
    if (lowerCond.includes('excellent') || lowerCond.includes('like new') || lowerCond.includes('mint')) return 'excellent';
    if (lowerCond.includes('good') || lowerCond.includes('very good')) return 'good';
    if (lowerCond.includes('fair') || lowerCond.includes('acceptable')) return 'fair';
    return 'good';
};

const recommendPackaging = (dimensions, weight, fragility) => {
    if (!dimensions || !weight) {
        return {
            recommended: 'small-parcel',
            details: SPICYLISTER_PACKAGING['small-parcel'],
            reasoning: 'Default recommendation - dimensions not available',
            alternatives: [SPICYLISTER_PACKAGING['medium-parcel']]
        };
    }

    const { length, width, height } = dimensions;
    const weightGrams = weight.grams || weight;
    const paddedLength = length + 3;
    const paddedWidth = width + 3;
    const paddedHeight = height + 3;
    let recommended = 'large-parcel';
    let reasoning = 'Fits safely with protective padding';
    const packagingOrder = ['large-letter', 'small-parcel', 'medium-parcel', 'large-parcel'];

    for (const size of packagingOrder) {
        const pkg = SPICYLISTER_PACKAGING[size];
        const max = pkg.maxDimensions;
        if (paddedLength <= max.length && paddedWidth <= max.width && paddedHeight <= max.height && weightGrams <= pkg.maxWeight) {
            recommended = size;
            break;
        }
    }

    if (fragility === 'high') {
        const currentIndex = packagingOrder.indexOf(recommended);
        if (currentIndex < packagingOrder.length - 1 && recommended !== 'large-parcel') {
            recommended = packagingOrder[currentIndex + 1];
            reasoning = 'Upsized for fragile item protection';
        }
    }

    const currentIndex = packagingOrder.indexOf(recommended);
    const alternatives = [];
    if (currentIndex < packagingOrder.length - 1) {
        alternatives.push(SPICYLISTER_PACKAGING[packagingOrder[currentIndex + 1]]);
    }

    return {
        recommended,
        details: SPICYLISTER_PACKAGING[recommended],
        reasoning,
        alternatives
    };
};

const calculateSavings = () => {
    const saved = JSON.parse(localStorage.getItem('spicylister_savings') || '{"count": 0, "time": 0, "cost": 0}');
    saved.count += 1;
    saved.time += SAVINGS_PER_LISTING.time;
    saved.cost += SAVINGS_PER_LISTING.cost;
    localStorage.setItem('spicylister_savings', JSON.stringify(saved));
    return saved;
};

const getCumulativeSavings = () => {
    return JSON.parse(localStorage.getItem('spicylister_savings') || '{"count": 0, "time": 0, "cost": 0}');
};

const getRandomReward = () => {
    return REWARDS[Math.floor(Math.random() * REWARDS.length)];
};

const checkMilestones = (count) => {
    const milestones = [1, 5, 10, 25, 50, 100];
    if (milestones.includes(count)) {
        return {
            show: true,
            message: `🎊 ${count} listing${count > 1 ? 's' : ''} created! You're crushing it!`,
            confetti: true
        };
    }
    return { show: false };
};

const awardKudos = async (action, setShowKudosNotification) => {
    const { kudos, coins } = KUDOS_ACTIONS[action] || { kudos: 5, coins: 2 };
    setShowKudosNotification({ kudos, coins });
    setTimeout(() => setShowKudosNotification(null), 3000);
};

const OID_MODES = {
    vinyl: {
        label: '🎵 Vinyl',
        name: 'Vinyl',
        prompt: 'SPECIALIST MODE — VINYL RECORDS: Apply Goldmine grading scale (Mint/Near Mint/Very Good Plus/Very Good/Good Plus/Good/Fair/Poor). Grade the disc and sleeve SEPARATELY. Note pressing country, label name, catalogue number, matrix/deadwax inscription if visible. Check for scratches, scuffs, label damage, ring wear on sleeve, seam splits.'
    },
    magic: {
        label: '🪄 Magic',
        name: 'Magic',
        prompt: 'SPECIALIST MODE — MAGIC PROPS: Identify the maker/manufacturer and effect type (card trick, silk, mental, stage, close-up, parlour etc). Assess completeness — note what should be included (props, gimmick, instructions, DVD, original box) and what appears to be present or missing. Note any wear to the gimmick or working mechanism.'
    },
    books: {
        label: '📚 Books',
        name: 'Books',
        prompt: 'SPECIALIST MODE — BOOKS: Apply antiquarian book grading (Fine/Near Fine/Very Good Plus/Very Good/Good Plus/Good/Fair/Poor). Grade dust jacket separately from the book block. Note edition (first/later), printing number, publisher, year. Note any inscriptions, bookplates, stamps, foxing, spine fading, bumped corners, tanning.'
    }
};

// =============================================================================
// GIFT CODE GENERATOR — Founder only (real Patreon session, server-verified)
// =============================================================================

function GiftCodeGenerator() {
    const [tier, setTier] = React.useState('elder');
    const [count, setCount] = React.useState(10);
    const [codes, setCodes] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [copied, setCopied] = React.useState(null);

    const generate = async () => {
        setLoading(true);
        setCodes([]);
        try {
            const session = JSON.parse(localStorage.getItem('patreon_session') || '{}');
            const res = await fetch('/.netlify/functions/gift-generate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ session, tier, count, note: 'Germany tour 2026' })
            });
            const data = await res.json();
            if (data.ok) setCodes(data.codes);
        } catch {}
        setLoading(false);
    };

    const copyAll = () => {
        const base = `${window.location.origin}/gift`;
        const lines = codes.map(c => `${base}/${c}?tier=${tier}`).join('\n');
        navigator.clipboard.writeText(lines);
        setCopied('all');
        setTimeout(() => setCopied(null), 2000);
    };

    const base = typeof window !== 'undefined' ? `${window.location.origin}/gift` : 'https://spicylister.com/gift';

    return (
        <div style={{background:'#f0fdf4',border:'2px solid #16a34a',borderRadius:'1rem',padding:'1rem',marginTop:'0.75rem'}}>
            <p style={{fontWeight:800,color:'#14532d',fontSize:'0.85rem',marginBottom:'0.75rem'}}>🎟️ Germany Gift Codes</p>
            <div style={{display:'flex',gap:'0.5rem',flexWrap:'wrap',marginBottom:'0.75rem'}}>
                <select value={tier} onChange={e=>setTier(e.target.value)}
                    style={{border:'2px solid #16a34a',borderRadius:'0.5rem',padding:'0.4rem 0.6rem',fontSize:'0.82rem',fontWeight:700,background:'#fff'}}>
                    <option value="elder">⭐ Elder — individual</option>
                    <option value="founder">🌿 Founder — club</option>
                </select>
                <select value={count} onChange={e=>setCount(parseInt(e.target.value))}
                    style={{border:'2px solid #16a34a',borderRadius:'0.5rem',padding:'0.4rem 0.6rem',fontSize:'0.82rem',fontWeight:700,background:'#fff'}}>
                    {[5,10,20,30,50].map(n=><option key={n} value={n}>{n} codes</option>)}
                </select>
                <button onClick={generate} disabled={loading}
                    style={{background:'#16a34a',color:'#fff',border:'none',borderRadius:'0.5rem',padding:'0.4rem 0.9rem',fontWeight:800,fontSize:'0.82rem',cursor:'pointer',opacity:loading?0.5:1}}>
                    {loading ? 'Generating…' : 'Generate'}
                </button>
            </div>
            {codes.length > 0 && (
                <div>
                    <div style={{background:'#fff',borderRadius:'0.6rem',padding:'0.6rem',maxHeight:'180px',overflowY:'auto',fontSize:'0.72rem',fontFamily:'monospace',marginBottom:'0.5rem'}}>
                        {codes.map(c=>(
                            <div key={c} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'0.2rem 0'}}>
                                <span>{base}/{c}?tier={tier}</span>
                                <button onClick={()=>{navigator.clipboard.writeText(`${base}/${c}?tier=${tier}`);setCopied(c);setTimeout(()=>setCopied(null),1500);}}
                                    style={{background:'none',border:'none',cursor:'pointer',fontSize:'0.7rem',color:copied===c?'#16a34a':'#78716c',fontWeight:700}}>
                                    {copied===c?'✓':'Copy'}
                                </button>
                            </div>
                        ))}
                    </div>
                    <button onClick={copyAll}
                        style={{background:'#14532d',color:'#fff',border:'none',borderRadius:'0.5rem',padding:'0.4rem 0.9rem',fontWeight:800,fontSize:'0.82rem',cursor:'pointer',width:'100%'}}>
                        {copied==='all'?'✓ Copied all!':'📋 Copy all links'}
                    </button>
                </div>
            )}
        </div>
    );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function App() {
    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState(null);
    const [isSpicyMode, setIsSpicyMode] = useState(true);
    const [showConfetti, setShowConfetti] = useState(false);
    const [copiedSection, setCopiedSection] = useState(null);
    const [copiedStore, setCopiedStore] = useState(false);
    const [editingDimensions, setEditingDimensions] = useState(false);
    const [editedDimensions, setEditedDimensions] = useState(null);
    const [editingPrice, setEditingPrice] = useState(false);
    const [editedPrice, setEditedPrice] = useState(null);
    const [dimensionWarning, setDimensionWarning] = useState(null);
    const [packaging, setPackaging] = useState(null);
    const [hasOwnPackaging, setHasOwnPackaging] = useState(false);
    const [currentSavings, setCurrentSavings] = useState(null);
    const [currentReward, setCurrentReward] = useState(null);
    const [milestone, setMilestone] = useState(null);
    const [isPro, setIsPro] = useState(false);
    const [patreonTier, setPatreonTier] = useState(null);
    const [userEmail, setUserEmail] = useState('');
    const [creditsRemaining, setCreditsRemaining] = useState(0);
    const [dailyCreditsRemaining, setDailyCreditsRemaining] = useState(0);
    const [showCreditsModal, setShowCreditsModal] = useState(false);
    const [creditEmail, setCreditEmail] = useState('');
    const [creditsPurchased, setCreditsPurchased] = useState(false);
    const [listingCount, setListingCount] = useState(() => {
        const saved = localStorage.getItem('spicylister_count');
        return saved ? parseInt(saved) : 0;
    });
    const [hamletSlug, setHamletSlug] = useState(() => localStorage.getItem('spicylister_hamlet_slug') || '');
    const [hamletSlugInput, setHamletSlugInput] = useState('');
    const [hamletPaypal, setHamletPaypal] = useState(() => localStorage.getItem('spicylister_paypal_me') || '');
    const [hamletWhatsapp, setHamletWhatsapp] = useState(() => localStorage.getItem('spicylister_whatsapp') || '');
    const [hamletEbayUrl, setHamletEbayUrl] = useState('');
    const [showHamletSetup, setShowHamletSetup] = useState(false);
    const [hamletSaveStatus, setHamletSaveStatus] = useState(null);
    const [bulkHamletStatus, setBulkHamletStatus] = useState(null);
    const [showKudosNotification, setShowKudosNotification] = useState(null);
    const [showEbayToast, setShowEbayToast] = useState(false);
    const [patreonToast, setPatreonToast] = useState(null);
    const [globalCount, setGlobalCount] = useState(null);
    const [items, setItems] = useState([]);
    const [bulkProcessing, setBulkProcessing] = useState(false);
    const [processProgress, setProcessProgress] = useState({ current: 0, total: 0 });
    const [showGrid, setShowGrid] = useState(false);
    const [bulkContext, setBulkContext] = useState('');
    const [selectedMode, setSelectedMode] = useState(null);
    const [waitingMessage, setWaitingMessage] = useState(null);
    const [lang, setLang] = useState(detectUserLanguage);

    const t = (key) => {
        const tr = TRANSLATIONS[lang] || TRANSLATIONS.en;
        return tr[key] !== undefined ? tr[key] : (TRANSLATIONS.en[key] || key);
    };

    const switchLang = (l) => {
        setLang(l);
        try { localStorage.setItem('spicylister_lang', l); } catch {}
    };

    const resultCardRef = useRef(null);
    const userRegion = detectUserRegion();
    const userCurrency = GLOBAL_REGIONS[userRegion];

    useEffect(() => {
        const saved = getCumulativeSavings();
        if (saved.count > 0) {
            setCurrentSavings(saved);
        }
    }, []);

    // Restore last generated listing so navigating to eBay (or any tab switch that
    // kills the background tab on mobile) doesn't wipe out the user's work.
    // On an intentional browser refresh (reload) we start clean instead.
    useEffect(() => {
        try {
            const raw = localStorage.getItem('spicylister_auto_save');
            if (!raw) return;
            const navType = window.performance?.getEntriesByType('navigation')?.[0]?.type;
            if (navType === 'reload') {
                localStorage.removeItem('spicylister_auto_save');
                return;
            }
            const saved = JSON.parse(raw);
            const THIRTY_MINS = 30 * 60 * 1000;
            if (saved.savedAt && Date.now() - saved.savedAt < THIRTY_MINS) {
                if (saved.results) setResults(saved.results);
                if (saved.imagePreview) setImagePreview(saved.imagePreview);
            } else {
                localStorage.removeItem('spicylister_auto_save');
            }
        } catch (e) {
            localStorage.removeItem('spicylister_auto_save');
        }
    }, []);

    // Persist listing to localStorage whenever it changes so it survives tab kills.
    useEffect(() => {
        if (!results) return;
        try {
            localStorage.setItem('spicylister_auto_save', JSON.stringify({
                results,
                imagePreview,
                savedAt: Date.now()
            }));
        } catch (e) {
            // Retry without the image if quota is exceeded (imagePreview can be ~200 KB)
            try {
                localStorage.setItem('spicylister_auto_save', JSON.stringify({
                    results,
                    imagePreview: null,
                    savedAt: Date.now()
                }));
            } catch (e2) { /* nothing more we can do */ }
        }
    }, [results, imagePreview]);

    // Restore a multi-item batch across a refresh — free for everyone.
    // Losing your work isn't a feature that should be reserved for paying members.
    useEffect(() => {
        try {
            const raw = sessionStorage.getItem('spicylister_items_autosave');
            if (!raw) return;
            const saved = JSON.parse(raw);
            if (saved.items?.length) {
                setItems(saved.items);
                setShowGrid(true);
            }
        } catch (e) {
            sessionStorage.removeItem('spicylister_items_autosave');
        }
    }, []);

    // Persist the multi-item batch across a refresh — free for everyone.
    useEffect(() => {
        if (items.length === 0) return;
        try {
            sessionStorage.setItem('spicylister_items_autosave', JSON.stringify({ items, savedAt: Date.now() }));
        } catch (e) {
            // Quota exceeded on a big batch of photos — retry stripping previews so the listing text still survives
            try {
                const stripped = items.map(i => ({ ...i, preview: null }));
                sessionStorage.setItem('spicylister_items_autosave', JSON.stringify({ items: stripped, savedAt: Date.now() }));
            } catch (e2) { /* nothing more we can do */ }
        }
    }, [items]);

    useEffect(() => {
        const fetchGlobalCount = async () => {
            try {
                const response = await fetch('/.netlify/functions/counter');
                const data = await response.json();
                if (typeof data.count === 'number') setGlobalCount(data.count);
            } catch (err) {
                console.log('Could not fetch global count:', err);
                setGlobalCount(null);
            }
        };
        fetchGlobalCount();
    }, []);

    // Restore Patreon session from localStorage (24hr expiry)
    useEffect(() => {
        try {
            const raw = localStorage.getItem('patreon_session');
            if (raw) {
                const session = JSON.parse(raw);
                if (session.expires && Date.now() < session.expires) {
                    setIsPro(session.isPro || false);
                    setPatreonTier(session.tier || null);
                    if (session.email) {
                        setUserEmail(session.email);
                        setCreditEmail(session.email);
                        fetch(`/.netlify/functions/check-credits?email=${encodeURIComponent(session.email)}`)
                            .then(r => r.json())
                            .then(d => { setCreditsRemaining(d.credits_remaining || 0); setDailyCreditsRemaining(d.daily_credits_remaining || 0); })
                            .catch(() => {});
                    }
                } else {
                    localStorage.removeItem('patreon_session');
                }
            }
        } catch (e) {
            localStorage.removeItem('patreon_session');
        }
    }, []);

    // Handle /auth/patreon OAuth redirect (?code=XXX)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        if (!code) return;

        // Clean the URL immediately
        window.history.replaceState(null, '', window.location.pathname);

        fetch(`/.netlify/functions/patreon-auth?code=${encodeURIComponent(code)}`)
            .then((r) => r.json())
            .then((data) => {
                if (data.error) {
                    console.error('Patreon auth returned error:', data.error, data.detail || '');
                    setPatreonToast({ status: 'error', message: 'Login failed — ' + (data.error || 'unknown error') });
                    setTimeout(() => setPatreonToast(null), 5000);
                    return;
                }
                if (data.isPro !== undefined) {
                    setIsPro(data.isPro);
                    setPatreonTier(data.tier || null);
                    if (data.email) {
                        setUserEmail(data.email);
                        setCreditEmail(data.email);
                        // Sync Patreon tier + fetch credits
                        fetch('/.netlify/functions/check-credits', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ email: data.email, action: 'patreon-sync', tier: data.tier })
                        }).then(r => r.json())
                          .then(d => { setCreditsRemaining(d.credits_remaining || 0); setDailyCreditsRemaining(d.daily_credits_remaining || 0); })
                          .catch(() => {});
                    }
                    // Use the server-issued session as-is — it carries the signed
                    // `sig` + `expires` from patreon-auth.js. Don't reconstruct it
                    // client-side or the signature verification on save/update/
                    // delete/gift-generate will reject it.
                    const session = {
                        isPro: data.isPro,
                        tier: data.tier || null,
                        email: data.email || null,
                        expires: data.expires || (Date.now() + 24 * 60 * 60 * 1000),
                        sig: data.sig
                    };
                    localStorage.setItem('patreon_session', JSON.stringify(session));
                    const tierLabel = data.tier === 'founder' ? '🏛️ Founder' : data.tier === 'elder' ? '⚔️ Elder' : data.tier === 'villager' ? '🏡 Villager' : null;
                    if (tierLabel) {
                        setPatreonToast({ status: 'success', message: `${tierLabel} — you're in!` });
                    } else if (!data.isPro) {
                        setPatreonToast({ status: 'none', message: "Logged in — no active Patreon membership found" });
                    }
                    setTimeout(() => setPatreonToast(null), 4000);
                }

                // Restore any results that were saved before the OAuth redirect
                try {
                    const saved = sessionStorage.getItem('spicylister_pending_results');
                    if (saved) {
                        const { savedResults, savedImagePreview } = JSON.parse(saved);
                        if (savedResults) setResults(savedResults);
                        if (savedImagePreview) setImagePreview(savedImagePreview);
                        sessionStorage.removeItem('spicylister_pending_results');
                    }
                } catch (e) {
                    sessionStorage.removeItem('spicylister_pending_results');
                }
            })
            .catch((err) => console.error('Patreon auth error:', err));
    }, []);

    // Handle Stripe payment success redirect (?credits_purchased=true&email=x)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('credits_purchased') === 'true') {
            window.history.replaceState(null, '', window.location.pathname);
            setCreditsPurchased(true);
            const returnedEmail = params.get('email') || userEmail;
            if (returnedEmail) {
                setUserEmail(returnedEmail);
                setCreditEmail(returnedEmail);
                fetch(`/.netlify/functions/check-credits?email=${encodeURIComponent(returnedEmail)}`)
                    .then(r => r.json())
                    .then(d => { setCreditsRemaining(d.credits_remaining || 0); setDailyCreditsRemaining(d.daily_credits_remaining || 0); })
                    .catch(() => {});
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle Stripe membership Payment Link return (?membership_checkout=SESSION_ID)
    // Mirrors the Patreon ?code= handler above — same session shape, same
    // localStorage key, so every downstream consumer (hamlet-listings-*,
    // gift-generate.js) treats a Stripe membership exactly like a Patreon one.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sessionId = params.get('membership_checkout');
        if (!sessionId) return;

        window.history.replaceState(null, '', window.location.pathname);

        // A retry, not a guarantee — the browser can beat Stripe's webhook to
        // Supabase by a second or two. One retry after a short wait covers
        // that without making the user do anything.
        const fetchStatus = (attempt = 0) => fetch(`/.netlify/functions/membership-status?session_id=${encodeURIComponent(sessionId)}`)
            .then((r) => r.json())
            .then((data) => {
                if (!data.error && !data.isPro && attempt === 0) {
                    return new Promise((resolve) => setTimeout(resolve, 2500)).then(() => fetchStatus(1));
                }
                return data;
            });

        fetchStatus()
            .then((data) => {
                if (data.error) {
                    console.error('membership-status returned error:', data.error);
                    setPatreonToast({ status: 'error', message: 'Could not confirm membership — try refreshing in a minute' });
                    setTimeout(() => setPatreonToast(null), 5000);
                    return;
                }
                setIsPro(data.isPro);
                setPatreonTier(data.tier || null);
                if (data.email) {
                    setUserEmail(data.email);
                    setCreditEmail(data.email);
                    fetch('/.netlify/functions/check-credits', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email: data.email, action: 'patreon-sync', tier: data.tier })
                    }).then(r => r.json())
                      .then(d => { setCreditsRemaining(d.credits_remaining || 0); setDailyCreditsRemaining(d.daily_credits_remaining || 0); })
                      .catch(() => {});
                }
                const session = { isPro: data.isPro, tier: data.tier || null, email: data.email || null, expires: data.expires, sig: data.sig };
                localStorage.setItem('patreon_session', JSON.stringify(session));
                const tierLabel = data.tier === 'founder' ? '🏛️ Founder' : data.tier === 'villager' ? '🏡 Villager' : null;
                if (tierLabel) {
                    setPatreonToast({ status: 'success', message: `${tierLabel} — you're in!` });
                } else {
                    setPatreonToast({ status: 'none', message: "Payment received, but we couldn't confirm membership yet — try refreshing in a minute" });
                }
                setTimeout(() => setPatreonToast(null), 4000);
            })
            .catch((err) => {
                console.error('membership-status fetch error:', err);
                setPatreonToast({ status: 'error', message: 'Could not confirm membership — try refreshing in a minute' });
                setTimeout(() => setPatreonToast(null), 5000);
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const hash = window.location.hash;
        if (!hash.startsWith('#oracle=')) return;
        try {
            const encoded = hash.slice(8);
            const json = decodeURIComponent(escape(atob(encoded)));
            const oracle = JSON.parse(json);
            if (oracle.source !== 'oracle') return;

            let priceLow = 0, priceHigh = 0;
            if (oracle.priceRange) {
                const prices = oracle.priceRange.match(/[\d,.]+/g);
                if (prices && prices.length >= 2) {
                    priceLow = parseFloat(prices[0].replace(',', ''));
                    priceHigh = parseFloat(prices[1].replace(',', ''));
                } else if (prices && prices.length === 1) {
                    priceLow = parseFloat(prices[0].replace(',', ''));
                    priceHigh = priceLow;
                }
            }

            const conditionMap = { 'mint': 'New', 'excellent': 'Excellent', 'very good': 'Very good', 'good': 'Good', 'fair': 'Fair', 'poor': 'Fair' };
            const rawCondition = (oracle.condition || '').toLowerCase();
            const condition = conditionMap[rawCondition] || oracle.condition || 'Good';

            const oracleResults = {
                title: oracle.itemName || oracle.title || 'Oracle Import',
                description: oracle.description || '',
                condition: condition,
                category: 'Collectables & Art',
                rarity: oracle.rarity || 'Uncommon',
                spicyComment: oracle.era ? `${oracle.era}${oracle.manufacturer ? ' by ' + oracle.manufacturer : ''} — identified by the Magic-Oid Oracle` : 'Identified by the Magic-Oid Oracle',
                priceLow: priceLow,
                priceHigh: priceHigh,
                dimensions: null,
                weight: null,
                material: null,
                fragility: 'medium',
                recommendedPackaging: null
            };

            const oracleItem = {
                id: Date.now() + Math.random(),
                file: null,
                preview: null,
                status: 'done',
                results: oracleResults,
                packaging: null,
                error: null,
                oracleImport: true
            };

            setItems([oracleItem]);
            setShowGrid(true);
            window.history.replaceState(null, '', window.location.pathname);
        } catch (e) {
            console.log('Oracle import failed:', e);
        }
    }, []);

    const getRarityStyle = (tier) => {
        return RARITY_TIERS[tier] || RARITY_TIERS['Common'];
    };

    const handleImageUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        if (files.length === 1) {
            const file = files[0];
            if (!file.type.startsWith('image/')) return;
            setImage(file);
            const compressed = await compressImage(file);
            setImagePreview(compressed);
            setResults(null);
            setShowConfetti(false);
            setPackaging(null);
            setCurrentSavings(null);
            setCurrentReward(null);
            setMilestone(null);
            setEditingDimensions(false);
            setEditedDimensions(null);
            setEditingPrice(false);
            setEditedPrice(null);
            setHasOwnPackaging(false);
            setItems([]);
            setShowGrid(false);
            return;
        }

        const newItems = [];
        for (const file of files) {
            if (!file.type.startsWith('image/')) continue;
            const compressed = await compressImage(file);
            newItems.push({
                id: Date.now() + Math.random(),
                file,
                preview: compressed,
                status: 'pending',
                results: null,
                packaging: null,
                error: null
            });
        }
        setItems(newItems);
        setShowGrid(true);
        setImage(null);
        setImagePreview(null);
        setResults(null);
        setShowConfetti(false);
    };

    const resetApp = () => {
        localStorage.removeItem('spicylister_auto_save');
        try { sessionStorage.removeItem('spicylister_pending_results'); } catch {};
        try { sessionStorage.removeItem('spicylister_items_autosave'); } catch {};
        setImage(null);
        setImagePreview(null);
        setResults(null);
        setShowConfetti(false);
        setPackaging(null);
        setCurrentSavings(null);
        setCurrentReward(null);
        setMilestone(null);
        setEditingDimensions(false);
        setEditedDimensions(null);
        setEditingPrice(false);
        setEditedPrice(null);
        setHasOwnPackaging(false);
        setDimensionWarning(null);
        setItems([]);
        setShowGrid(false);
        setBulkProcessing(false);
        setProcessProgress({ current: 0, total: 0 });
        setBulkContext('');
        setWaitingMessage(null);
    };

    // Returns true if the user can proceed. Shows appropriate modal and returns false if not.
    // Fails open if Supabase isn't configured (env vars not set yet).
    const consumeCredit = async (email) => {
        try {
            const res = await fetch('/.netlify/functions/check-credits', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, action: 'use' })
            });
            if (res.status === 402) {
                setShowCreditsModal(true);
                return false;
            }
            const d = await res.json();
            if (d.credits_remaining !== undefined) setCreditsRemaining(d.credits_remaining);
            if (d.daily_credits_remaining !== undefined) setDailyCreditsRemaining(d.daily_credits_remaining);
            return true;
        } catch {
            return true; // Supabase not configured — fail open
        }
    };

    const analyzeItem = async () => {
        if (!image) return;

        // Web generates are free for everyone — credits are reserved for future Lester integration
        setLoading(true);

        try {
            const base64Data = imagePreview.split(',')[1];
            const response = await fetch('/.netlify/functions/analyze-item', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    images: [{ mimeType: 'image/jpeg', data: base64Data }],
                    isSpicyMode: isSpicyMode,
                    region: userRegion,
                    language: lang,
                    extraInfo: (selectedMode && OID_MODES[selectedMode])
                        ? OID_MODES[selectedMode].prompt
                        : undefined
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `Server returned ${response.status}`);
            }

            const data = await response.json();
            data.priceLow = Number(data.priceLow) || 0;
            data.priceHigh = Number(data.priceHigh) || 0;

            if (!data.dimensions) data.dimensions = { length: 15, width: 10, height: 5, confidence: 50 };
            if (!data.weight) data.weight = { grams: 200, confidence: 50 };
            if (!data.fragility) data.fragility = 'medium';
            if (!data.material) data.material = 'Mixed materials';
            if (!data.condition) data.condition = 'Good condition';

            setResults(data);
            setEditedDimensions(data.dimensions);

            const packagingRec = recommendPackaging(data.dimensions, data.weight, data.fragility);
            setPackaging(packagingRec);

            const savings = calculateSavings();
            setCurrentSavings(savings);
            setCurrentReward(getRandomReward());

            const newCount = listingCount + 1;
            setListingCount(newCount);
            localStorage.setItem('spicylister_count', newCount.toString());

            const milestoneCheck = checkMilestones(newCount);
            if (milestoneCheck.show) setMilestone(milestoneCheck);

            await awardKudos('listing_created', setShowKudosNotification);

            try {
                const response = await fetch('/.netlify/functions/counter', { method: 'POST' });
                const data = await response.json();
                setGlobalCount(data.count);
            } catch (err) {
                console.log('Could not increment global count:', err);
            }

            if (isSpicyMode && (data.priceHigh > 50 || ['Rare', 'Legendary', 'God-Tier', 'Epic'].includes(data.rarity))) {
                setShowConfetti(true);
                setTimeout(() => setShowConfetti(false), 5000);
            }

            if (milestoneCheck.confetti) {
                setShowConfetti(true);
                setTimeout(() => setShowConfetti(false), 5000);
            }


        } catch (error) {
            console.error(error);
            let msg = "Something went wrong.";
            if (error.message.includes("429")) msg = "Too many requests! The AI is overwhelmed. Try again in a moment.";
            if (error.message.includes("API")) msg = "API issue. The server might be busy.";
            if (error.message.includes("network") || error.message.includes("fetch")) msg = "Network error. Check your connection.";
            alert(msg + "\nTechnical detail: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    // ==========================================================================
    // BULK PROCESSING
    // ==========================================================================

    const processAllItems = async () => {
        if (items.length === 0) return;
        setBulkProcessing(true);
        setProcessProgress({ current: 0, total: items.length });
        setWaitingMessage(getWaitingMessage());

        let delay = 4000;
        let consecutiveErrors = 0;

        const messageInterval = setInterval(() => {
            setWaitingMessage(getWaitingMessage());
        }, 8000);

        for (let i = 0; i < items.length; i++) {
            if (items[i].status === 'done') {
                setProcessProgress({ current: i + 1, total: items.length });
                continue;
            }

            setItems(prev => prev.map((item, idx) =>
                idx === i ? { ...item, status: 'processing' } : item
            ));
            setProcessProgress({ current: i + 1, total: items.length });

            // Web generates are free for everyone, same as the single-item flow above —
            // credits are reserved for future Lester integration, not gating bulk on web.

            try {
                const base64Data = items[i].preview.split(',')[1];
                const response = await fetch('/.netlify/functions/analyze-item', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        images: [{ mimeType: 'image/jpeg', data: base64Data }],
                        isSpicyMode: isSpicyMode,
                        region: userRegion,
                        language: lang,
                        extraInfo: (() => {
                            const parts = [];
                            if (selectedMode && OID_MODES[selectedMode]) parts.push(OID_MODES[selectedMode].prompt);
                            if (bulkContext) parts.push(bulkContext);
                            return parts.length ? parts.join(' ') : undefined;
                        })()
                    })
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    const errorMsg = errorData.error || `Server returned ${response.status}`;

                    if (response.status === 429 || errorMsg.includes('Too many requests')) {
                        consecutiveErrors++;
                        delay = Math.min(delay * 2, 30000);
                        setWaitingMessage({
                            type: 'tip',
                            text: `Slowing down to avoid rate limits... (${Math.round(delay / 1000)}s between items)`
                        });
                        await new Promise(r => setTimeout(r, delay));
                        i--;
                        continue;
                    }

                    throw new Error(errorMsg);
                }

                const data = await response.json();
                data.priceLow = Number(data.priceLow) || 0;
                data.priceHigh = Number(data.priceHigh) || 0;
                if (!data.dimensions) data.dimensions = { length: 15, width: 10, height: 5, confidence: 50 };
                if (!data.weight) data.weight = { grams: 200, confidence: 50 };
                if (!data.fragility) data.fragility = 'medium';
                if (!data.material) data.material = 'Mixed materials';
                if (!data.condition) data.condition = 'Good condition';

                const packagingRec = recommendPackaging(data.dimensions, data.weight, data.fragility);

                setItems(prev => prev.map((item, idx) =>
                    idx === i ? { ...item, status: 'done', results: data, packaging: packagingRec } : item
                ));

                consecutiveErrors = 0;
                if (delay > 4000) delay = Math.max(delay - 2000, 4000);

                if (i < items.length - 1) {
                    await new Promise(r => setTimeout(r, delay));
                }
            } catch (err) {
                console.error(`Error processing item ${i}:`, err);
                consecutiveErrors++;

                if (consecutiveErrors >= 3) {
                    delay = Math.min(delay * 2, 30000);
                    setWaitingMessage({
                        type: 'tip',
                        text: `Hit a few bumps - taking it easy... (${Math.round(delay / 1000)}s between items)`
                    });
                }

                setItems(prev => prev.map((item, idx) =>
                    idx === i ? { ...item, status: 'error', error: err.message } : item
                ));

                if (i < items.length - 1) {
                    await new Promise(r => setTimeout(r, delay));
                }
            }
        }

        clearInterval(messageInterval);
        setBulkProcessing(false);
        setWaitingMessage(null);

        const doneCount = items.filter(it => it.results).length;
        if (doneCount > 0) {
            const saved = JSON.parse(localStorage.getItem('spicylister_savings') || '{"count": 0, "time": 0, "cost": 0}');
            saved.count += doneCount;
            saved.time += doneCount * SAVINGS_PER_LISTING.time;
            saved.cost += doneCount * SAVINGS_PER_LISTING.cost;
            localStorage.setItem('spicylister_savings', JSON.stringify(saved));
            setCurrentSavings(saved);
            setCurrentReward(getRandomReward());
        }

        try {
            for (let j = 0; j < doneCount; j++) {
                await fetch('/.netlify/functions/counter', { method: 'POST' });
            }
            const counterRes = await fetch('/.netlify/functions/counter');
            const counterData = await counterRes.json();
            setGlobalCount(counterData.count);
        } catch (err) {
            console.log('Could not update global count:', err);
        }
    };

    const retryItem = async (index) => {
        // Free for everyone, same as analyzeItem and processAllItems above.
        setItems(prev => prev.map((item, i) =>
            i === index ? { ...item, status: 'processing', error: null } : item
        ));

        try {
            const base64Data = items[index].preview.split(',')[1];
            const response = await fetch('/.netlify/functions/analyze-item', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    images: [{ mimeType: 'image/jpeg', data: base64Data }],
                    isSpicyMode: isSpicyMode,
                    region: userRegion,
                    language: lang,
                    extraInfo: (() => {
                        const parts = [];
                        if (selectedMode && OID_MODES[selectedMode]) parts.push(OID_MODES[selectedMode].prompt);
                        if (bulkContext) parts.push(bulkContext);
                        return parts.length ? parts.join(' ') : undefined;
                    })()
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `Server returned ${response.status}`);
            }

            const data = await response.json();
            data.priceLow = Number(data.priceLow) || 0;
            data.priceHigh = Number(data.priceHigh) || 0;
            if (!data.dimensions) data.dimensions = { length: 15, width: 10, height: 5, confidence: 50 };
            if (!data.weight) data.weight = { grams: 200, confidence: 50 };
            if (!data.fragility) data.fragility = 'medium';
            if (!data.material) data.material = 'Mixed materials';
            if (!data.condition) data.condition = 'Good condition';

            const packagingRec = recommendPackaging(data.dimensions, data.weight, data.fragility);

            setItems(prev => prev.map((item, idx) =>
                idx === index ? { ...item, status: 'done', results: data, packaging: packagingRec } : item
            ));
        } catch (err) {
            setItems(prev => prev.map((item, idx) =>
                idx === index ? { ...item, status: 'error', error: err.message } : item
            ));
        }
    };

    const viewItemDetail = (index) => {
        const item = items[index];
        if (!item.results) return;
        setImage(item.file);
        setImagePreview(item.preview);
        setResults(item.results);
        setPackaging(item.packaging);
        setEditedDimensions(item.results.dimensions);
        setShowGrid(false);
    };

    const backToGrid = () => {
        setImage(null);
        setImagePreview(null);
        setResults(null);
        setPackaging(null);
        setEditingDimensions(false);
        setEditedDimensions(null);
        setEditingPrice(false);
        setEditedPrice(null);
        setHasOwnPackaging(false);
        setShowGrid(true);
    };

    const removeItem = (index) => {
        setItems(prev => prev.filter((_, i) => i !== index));
    };

    const exportCSV = () => {
        const doneItems = items.filter(item => item.status === 'done' && item.results);
        if (doneItems.length === 0) return;

        // #INFO rows matching official eBay draft template exactly
        const INFO_ROW_1 = '#INFO,Version=0.0.2,Template= eBay-draft-listings-template_GB,,,,,,,,';
        const INFO_ROW_2 = '#INFO Action and Category ID are required fields. 1) Set Action to Draft 2) Please find the category ID for your listings here: https://pages.ebay.com/sellerinformation/news/categorychanges.html,,,,,,,,,,';
        const INFO_ROW_3 = '"#INFO After you\'ve successfully uploaded your draft from the Seller Hub Reports tab, complete your drafts to active listings here: https://www.ebay.co.uk/sh/lst/drafts",,,,,,,,,,';
        const INFO_ROW_4 = '#INFO,,,,,,,,,,';

        const HEADERS = [
            'Action(SiteID=UK|Country=GB|Currency=GBP|Version=1193|CC=UTF-8)',
            'Custom label (SKU)',
            'Category ID',
            'Title',
            'UPC',
            'Price',
            'Quantity',
            'Item photo URL',
            'Condition ID',
            'Description',
            'Format',
        ];

        // eBay draft format uses text condition labels
        const conditionTextMap = {
            'new': 'NEW',
            'like-new': 'LIKE_NEW',
            'excellent': 'VERY_GOOD',
            'good': 'GOOD',
            'fair': 'ACCEPTABLE'
        };

        const csvEscape = (value) => {
            if (value === null || value === undefined) return '';
            const str = String(value);
            if (!/[",\n\r]/.test(str)) return str;
            return `"${str.replace(/"/g, '""')}"`;
        };

        const dataRows = doneItems.map(item => {
            const r = item.results;
            const condKey = mapCondition(r.condition);
            const rawPrice = parseFloat(r.priceLow);
            const price = (!rawPrice || rawPrice <= 0) ? '0.99' : rawPrice.toFixed(2);

            const values = [
                'Draft',
                '',
                '9800',       // Everything Else — change per item in Seller Hub
                (r.title || '').substring(0, 80),
                '',
                price,
                '1',
                '',
                conditionTextMap[condKey] || 'USED',
                (r.description || ''),
                'FixedPrice',
            ];
            return values.map(csvEscape).join(',');
        });

        const csvContent = [
            INFO_ROW_1,
            INFO_ROW_2,
            INFO_ROW_3,
            INFO_ROW_4,
            HEADERS.join(','),
            ...dataRows
        ].join('\r\n');

        const bom = '\uFEFF';
        const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `spicylister-drafts-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        setShowEbayToast(true);
        setTimeout(() => setShowEbayToast(false), 8000);
    };

    const applyBulkCondition = (condition) => {
        setItems(prev => prev.map(item =>
            item.status === 'done' && item.results
                ? { ...item, results: { ...item.results, condition } }
                : item
        ));
    };

    const applyBulkNote = (note) => {
        if (!note.trim()) return;
        setItems(prev => prev.map(item =>
            item.status === 'done' && item.results
                ? { ...item, results: { ...item.results, description: item.results.description + '\n\n' + note } }
                : item
        ));
    };

    const applyPriceAdjust = (percent) => {
        const multiplier = 1 + (percent / 100);
        setItems(prev => prev.map(item =>
            item.status === 'done' && item.results
                ? {
                    ...item,
                    results: {
                        ...item.results,
                        priceLow: Math.round(item.results.priceLow * multiplier * 100) / 100,
                        priceHigh: Math.round(item.results.priceHigh * multiplier * 100) / 100
                    }
                }
                : item
        ));
    };

    const sendToStore = () => {
        if (!results) return;
        const dimensions = editedDimensions || results.dimensions;
        const storeData = {
            title: results.title,
            description: results.description,
            category: mapCategory(results.category),
            priceGbp: Math.round((editedPrice?.priceLow ?? results.priceLow) * 100),
            condition: mapCondition(results.condition),
            dimensions: `${dimensions.length}x${dimensions.width}x${dimensions.height}`,
            weight: results.weight.grams,
            recommendedPackaging: packaging?.recommended || 'small-parcel',
            packagingPrice: packaging?.details?.price || 1.20,
            shippingMethod: 'royal-mail-48-tracked',
            shippingCost: 500
        };
        const encoded = encodeURIComponent(JSON.stringify(storeData));
        window.open(`https://spicylisterstore.manus.space/list?import=${encoded}`, '_blank');
    };

    const shareResult = async () => {
        if (resultCardRef.current) {
            try {
                const dataUrl = await toPng(resultCardRef.current, { cacheBust: true });
                const link = document.createElement('a');
                link.download = `spicylister-share.png`;
                link.href = dataUrl;
                link.click();
            } catch (err) { console.error(err); }
        }
    };

    // Persist current results across the Patreon OAuth redirect (used by both sign-in entry points below)
    const persistPendingResults = () => {
        try {
            sessionStorage.setItem('spicylister_pending_results', JSON.stringify({
                savedResults: results,
                savedImagePreview: imagePreview
            }));
        } catch (e) { /* quota exceeded — results will be lost but auth will work */ }
    };

    const copyText = (key, text) => {
        navigator.clipboard.writeText(text);
        setCopiedSection(key);
        setTimeout(() => setCopiedSection(null), 2000);
    };

    const copyForStore = async () => {
        if (!results) return;
        const dimensions = editedDimensions || results.dimensions;
        const storeText = `${results.title}

${results.description}

Condition: ${results.condition || 'Good condition'}

VALUE: ${userCurrency.symbol}${editedPrice?.priceLow ?? results.priceLow} - ${editedPrice?.priceHigh ?? results.priceHigh}
CATEGORY: ${results.category}

DIMENSIONS: ${dimensions.length}×${dimensions.width}×${dimensions.height}cm
WEIGHT: ~${results.weight.grams}g
PACKAGING: ${packaging?.details?.name || 'SpicyLister Small Box'}`;

        try {
            await navigator.clipboard.writeText(storeText);
            setCopiedStore(true);
            setTimeout(() => setCopiedStore(false), 2000);
        } catch (err) {
            console.error('Copy failed:', err);
        }
    };

    // Free "shop front" HTML block — paste into eBay's own listing HTML/description editor.
    // Inline styles only (eBay strips <style> tags and most classes from pasted HTML).
    // No <a href> links anywhere — eBay policy strips/rejects links in listing descriptions,
    // so the brand mention has to be plain text people can search for themselves.
    const buildShopFrontHtml = () => {
        if (!results) return '';
        const price = editedPrice?.priceLow ?? results.priceLow;
        const condition = results.condition || 'Good condition';
        return `<div style="font-family:Arial,Helvetica,sans-serif;max-width:600px;border:2px solid #f97316;border-radius:12px;overflow:hidden;">
  <div style="background:#f97316;color:#fff;padding:12px 16px;">
    <strong style="font-size:16px;">${results.title}</strong>
  </div>
  <div style="padding:16px;">
    <p style="margin:0 0 8px;font-size:13px;color:#78716c;">Condition: ${condition}</p>
    ${price ? `<p style="margin:0 0 8px;"><span style="background:#f97316;color:#fff;font-weight:bold;padding:3px 10px;border-radius:6px;">£${price}</span> <span style="font-size:12px;color:#78716c;">+ £5 postage · Royal Mail Tracked 24</span></p>` : ''}
    <p style="margin:0;font-size:14px;line-height:1.6;color:#292524;">${results.description}</p>
    <p style="margin:14px 0 0;font-size:11px;color:#a8a29e;">Shop front made with SpicyLister — sell your clutter without a stutter 🌶️ (search it, no link needed)</p>
  </div>
</div>`;
    };

    const copyShopFrontHtml = async () => {
        try {
            await navigator.clipboard.writeText(buildShopFrontHtml());
            setCopiedSection('shopfront');
            setTimeout(() => setCopiedSection(null), 2000);
        } catch (err) {
            console.error('Copy failed:', err);
        }
    };

    const handleDimensionChange = (field, value) => {
        const numValue = parseInt(value) || 0;
        const newDimensions = { ...editedDimensions, [field]: numValue };
        setEditedDimensions(newDimensions);

        if (results?.dimensions) {
            const originalValue = results.dimensions[field];
            const diff = Math.abs(numValue - originalValue);
            const percentDiff = (diff / originalValue) * 100;
            if (percentDiff > 50 && numValue > 0) {
                setDimensionWarning(`That's quite different from the AI estimate (${originalValue}cm). Are you sure?`);
            } else {
                setDimensionWarning(null);
            }
        }

        if (results?.weight && results?.fragility) {
            const newPackaging = recommendPackaging(newDimensions, results.weight, results.fragility);
            setPackaging(newPackaging);
        }
    };

    const saveDimensionEdits = () => {
        setEditingDimensions(false);
        setDimensionWarning(null);
        if (results?.weight && results?.fragility) {
            const newPackaging = recommendPackaging(editedDimensions, results.weight, results.fragility);
            setPackaging(newPackaging);
        }
    };

    const formatTime = (minutes) => {
        if (minutes < 60) return `${minutes} min`;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return mins > 0 ? `${hours}hr ${mins}min` : `${hours}hr`;
    };

    const saveToHamlet = async (slug) => {
        if (!results || !slug) return;
        setHamletSaveStatus('saving');
        try {
            const raw = localStorage.getItem('patreon_session');
            const session = JSON.parse(raw);
            const res = await fetch('/.netlify/functions/hamlet-listings-save', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    session,
                    hamlet_slug: slug,
                    listing: results,
                    paypal_me: localStorage.getItem('spicylister_paypal_me') || null,
                    whatsapp: localStorage.getItem('spicylister_whatsapp') || null,
                    ebay_url: hamletEbayUrl.trim() || null,
                    image: imagePreview || null
                })
            });
            const data = await res.json();
            if (data.ok) {
                setHamletSaveStatus('saved');
                setHamletEbayUrl('');
                setTimeout(() => setHamletSaveStatus(null), 4000);
            } else {
                setHamletSaveStatus('error');
                setTimeout(() => setHamletSaveStatus(null), 4000);
            }
        } catch {
            setHamletSaveStatus('error');
            setTimeout(() => setHamletSaveStatus(null), 4000);
        }
    };

    // Save every completed item in the current batch to the Founder's hamlet stall in one go —
    // one photo shoot of 50 books, one click, all 50 listed.
    const saveAllToHamlet = async () => {
        const doneItems = items.filter(i => i.status === 'done' && i.results);
        if (!doneItems.length || !hamletSlug) return;
        setBulkHamletStatus({ saving: true, done: 0, total: doneItems.length, failed: 0 });
        let raw, session;
        try {
            raw = localStorage.getItem('patreon_session');
            session = JSON.parse(raw);
        } catch { session = null; }
        const paypal_me = localStorage.getItem('spicylister_paypal_me') || null;
        const whatsapp = localStorage.getItem('spicylister_whatsapp') || null;
        let failed = 0;
        for (let i = 0; i < doneItems.length; i++) {
            const item = doneItems[i];
            try {
                const res = await fetch('/.netlify/functions/hamlet-listings-save', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        session,
                        hamlet_slug: hamletSlug,
                        listing: item.results,
                        paypal_me,
                        whatsapp,
                        ebay_url: null,
                        image: item.preview || null
                    })
                });
                const data = await res.json();
                if (!data.ok) failed++;
            } catch {
                failed++;
            }
            setBulkHamletStatus({ saving: true, done: i + 1, total: doneItems.length, failed });
        }
        setBulkHamletStatus({ saving: false, done: doneItems.length, total: doneItems.length, failed });
        setTimeout(() => setBulkHamletStatus(null), 6000);
    };

    const confirmHamletSlug = (slug, paypal, wa) => {
        const clean = slug.toLowerCase().trim().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
        if (!clean) return;
        localStorage.setItem('spicylister_hamlet_slug', clean);
        if (paypal) localStorage.setItem('spicylister_paypal_me', paypal.trim());
        if (wa) localStorage.setItem('spicylister_whatsapp', wa.trim());
        setHamletSlug(clean);
        setHamletPaypal(paypal || '');
        setHamletWhatsapp(wa || '');
        setShowHamletSetup(false);
        saveToHamlet(clean);
    };

    // RENDER
    // =============================================================================

    return (
        <div className="min-h-screen bg-gradient-to-br from-orange-100 via-orange-50 to-yellow-50 font-sans p-4">
            {showConfetti && <Confetti numberOfPieces={250} recycle={false} />}

            {showKudosNotification && (
                <div className="fixed top-4 right-4 z-50 animate-bounce">
                    <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-3 rounded-2xl shadow-lg">
                        <p className="font-bold text-lg">+{showKudosNotification.kudos} Kudos! 🌟</p>
                        <p className="text-xs opacity-90">You're helping build the community! 💚</p>
                    </div>
                </div>
            )}

            {patreonToast && (
                <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[92vw] max-w-sm">
                    <div className={`px-5 py-4 rounded-2xl shadow-xl text-white font-bold text-center ${patreonToast.status === 'success' ? 'bg-gradient-to-r from-purple-600 to-orange-500' : patreonToast.status === 'error' ? 'bg-red-600' : 'bg-gray-700'}`}>
                        {patreonToast.message}
                    </div>
                </div>
            )}

            {showEbayToast && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-[92vw] max-w-md bg-green-700 text-white rounded-2xl shadow-xl px-5 py-4">
                    <p className="font-bold text-base mb-1">🎉 Your eBay drafts are ready!</p>
                    <p className="text-sm text-green-100 mb-2">Upload at eBay Seller Hub → Reports → Upload.</p>
                    <p className="text-sm text-green-100 mb-3">Open each one on your phone to add photos, set shipping and publish.</p>
                    <a href="https://www.ebay.co.uk/sh/reports/uploads" target="_blank" rel="noreferrer" className="inline-block bg-white text-green-800 font-bold text-sm px-4 py-2 rounded-xl hover:bg-green-50 transition-colors">Open eBay Seller Hub →</a>
                </div>
            )}

            {/* CREDITS MODAL */}
            {showCreditsModal && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-end sm:items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl relative">
                        <button onClick={() => setShowCreditsModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
                        <h2 className="text-2xl font-black text-gray-900 mb-1">⚡ List it for me</h2>
                        <p className="text-gray-600 text-sm mb-1">AI files your listing to eBay while you make the coffee.</p>
                        <p className="text-amber-600 text-xs font-semibold mb-4">⚠️ Keep your eBay tab open — closing it loses the listing and the credit.</p>

                        {(creditsRemaining > 0 || dailyCreditsRemaining > 0) ? (
                            <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-4 mb-4 text-center">
                                <p className="font-bold text-green-800">You have credits ready</p>
                                {dailyCreditsRemaining > 0 && <p className="text-green-700 text-sm mt-1">Daily: {dailyCreditsRemaining} left today</p>}
                                {creditsRemaining > 0 && <p className="text-green-700 text-sm">Purchased: {creditsRemaining} (never expire)</p>}
                                <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3 text-left">
                                    <p className="text-xs font-bold text-amber-800 mb-1">How to use your credit:</p>
                                    <p className="text-xs text-amber-700">Open SpicyLister on your computer → the agent will file this listing to your eBay account automatically. Web auto-listing is coming soon.</p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-2 gap-2 mb-4">
                                    {[
                                        { pack: '1', label: '1 listing', price: '£1', sub: '£1 each' },
                                        { pack: '5', label: '5 listings', price: '£3', sub: '60p each' },
                                        { pack: '20', label: '20 listings', price: '£10', sub: '50p each' },
                                        { pack: '50', label: '50 listings', price: '£20', sub: '40p each', best: true }
                                    ].map(({ pack, label, price, sub, best }) => (
                                        <button key={pack} onClick={async () => {
                                            const em = creditEmail || userEmail;
                                            if (!em) { alert('Enter your email below first'); return; }
                                            const res = await fetch('/.netlify/functions/create-checkout', {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({ email: em, pack })
                                            });
                                            const data = await res.json();
                                            if (data.url) window.location.href = data.url;
                                        }} className={`relative flex flex-col items-center p-3 rounded-2xl border-2 transition-all ${best ? 'border-orange-400 bg-orange-50' : 'border-gray-200 hover:border-orange-300 bg-white'}`}>
                                            {best && <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">BEST</span>}
                                            <span className="font-black text-gray-900 text-lg">{price}</span>
                                            <span className="text-gray-700 text-sm font-semibold">{label}</span>
                                            <span className="text-gray-400 text-xs">{sub}</span>
                                        </button>
                                    ))}
                                </div>
                                <input
                                    type="email"
                                    placeholder="your@email.com"
                                    value={creditEmail}
                                    onChange={e => setCreditEmail(e.target.value)}
                                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm mb-3 focus:outline-none focus:border-orange-400"
                                />
                                <p className="text-gray-400 text-xs text-center mb-3">Credits never expire. Secure checkout via Stripe.</p>
                            </>
                        )}

                    </div>
                </div>
            )}

            <div className="max-w-2xl mx-auto">

                {/* HEADER & LOGO */}
                <div className="text-center mb-4 pt-2">
                    <div className="flex justify-center mb-2">
                        <img
                            src={process.env.PUBLIC_URL + '/logo.png'}
                            alt="SpicyLister Logo"
                            className="w-16 h-16 object-contain drop-shadow-md"
                            onError={(e) => { e.target.style.display = 'none' }}
                        />
                        <div className="text-6xl" style={{ display: 'none' }}>🌶️</div>
                    </div>

                    <h1 className="text-5xl font-bold mb-2" style={{ color: '#F28B82' }}>
                        SpicyLister
                    </h1>
                    {lang === 'en' ? (
                        <>
                            <p className="text-xl font-bold text-gray-800 mb-1">
                                {t('headline')}
                            </p>
                            <p className="text-sm text-gray-500 max-w-xs mx-auto leading-snug">
                                {t('tagline')}
                            </p>
                        </>
                    ) : (
                        <p className="text-xl font-medium text-gray-700">
                            {t('tagline')}
                        </p>
                    )}

                    {/* Language switcher */}
                    <div className="flex justify-center gap-1 mt-3">
                        {[['en','🇬🇧'],['de','🇩🇪'],['es','🇪🇸'],['fr','🇫🇷'],['pl','🇵🇱'],['it','🇮🇹']].map(([code, flag]) => (
                            <button
                                key={code}
                                onClick={() => switchLang(code)}
                                title={code.toUpperCase()}
                                className={`text-xl rounded-lg px-1.5 py-0.5 transition-all ${lang === code ? 'bg-orange-100 ring-2 ring-orange-400 scale-110' : 'opacity-50 hover:opacity-100'}`}
                            >
                                {flag}
                            </button>
                        ))}
                    </div>

                    <div className="flex justify-center mt-6">
                        <div className="bg-white p-1.5 rounded-full shadow-md inline-flex border border-orange-100">
                            <button
                                onClick={() => setIsSpicyMode(false)}
                                className={`px-6 py-2 rounded-full text-sm font-bold flex items-center gap-2 transition-all ${!isSpicyMode ? 'bg-blue-100 text-blue-700 shadow-inner' : 'text-gray-400 hover:text-gray-600'}`}
                            >
                                <IceCream size={18} /> {t('vanilla')}
                            </button>
                            <button
                                onClick={() => setIsSpicyMode(true)}
                                className={`px-6 py-2 rounded-full text-sm font-bold flex items-center gap-2 transition-all ${isSpicyMode ? 'bg-gradient-to-r from-orange-400 to-red-500 text-white shadow-md' : 'text-gray-400 hover:text-gray-600'}`}
                            >
                                <Flame size={18} /> {t('spicy')}
                            </button>
                        </div>
                    </div>

                    {isSpicyMode ? (
                        <p className="text-xs text-orange-600 mt-2 font-medium animate-pulse">{t('spicyOn')}</p>
                    ) : (
                        <p className="text-xs text-blue-500 mt-2 font-medium">{t('vanillaOn')}</p>
                    )}

                    {!isPro && (
                        <div className="mt-4 flex flex-col items-center gap-1">
                            <div className="flex items-center gap-3 flex-wrap justify-center">
                                <a
                                    href={STRIPE_VILLAGER_LINK}
                                    className="text-orange-600 text-xs font-bold hover:text-orange-700 underline"
                                    onClick={persistPendingResults}
                                >
                                    Become a Villager — 99p first month
                                </a>
                                <a
                                    href={STRIPE_FOUNDER_LINK}
                                    className="text-purple-600 text-xs font-bold hover:text-purple-700 underline"
                                    onClick={persistPendingResults}
                                >
                                    Become a Founder — £14.95/mo
                                </a>
                            </div>
                            <a
                                href={PATREON_OAUTH_URL}
                                className="text-gray-400 text-xs hover:text-gray-600 underline"
                                onClick={persistPendingResults}
                            >
                                Already support on Patreon? Sign in
                            </a>
                        </div>
                    )}
                    {isPro && patreonTier && (
                        <div className="mt-3 flex items-center gap-2 flex-wrap justify-center">
                            <span className="inline-block bg-purple-100 text-purple-700 text-xs font-bold px-3 py-1 rounded-full capitalize">
                                {patreonTier === 'founder' ? '🏛️ Founder' : patreonTier === 'elder' ? '⚔️ Elder' : '🏡 Villager'}
                            </span>
                        </div>
                    )}
                </div>

                {/* MAIN CARD */}
                <div className="bg-white rounded-3xl shadow-xl p-6 border-2 border-orange-100">

                    {!image && !showGrid && (
                        <div className="flex flex-col items-center justify-center w-full py-4">
                            <Camera className="w-12 h-12 mb-3 text-orange-300" />
                            <p className="font-bold text-xl mb-2 text-gray-700">{t('snapTitle')}</p>
                            <p className="text-sm text-gray-500 px-4 text-center mb-6">{t('snapSub')}</p>
                            <div className="flex gap-3 w-full max-w-xs">
                                <label className="flex-1 bg-gradient-to-r from-orange-400 to-red-500 text-white py-4 px-4 rounded-2xl font-bold text-center cursor-pointer hover:from-orange-500 hover:to-red-600 transition-all shadow-lg hover:scale-[1.02] active:scale-95">
                                    <span className="text-2xl block mb-1">📷</span>
                                    Camera
                                    <input type="file" className="hidden" accept="image/*" capture="environment" onChange={handleImageUpload} />
                                </label>
                                <label className="flex-1 bg-white border-2 border-orange-300 text-orange-600 py-4 px-4 rounded-2xl font-bold text-center cursor-pointer hover:bg-orange-50 transition-all shadow-md hover:scale-[1.02] active:scale-95">
                                    <span className="text-2xl block mb-1">🖼️</span>
                                    Gallery
                                    <input type="file" className="hidden" accept="image/*" multiple onChange={handleImageUpload} />
                                </label>
                            </div>
                        </div>
                    )}

                    {image && !results && !showGrid && (
                        <div className="space-y-6">
                            <div className="relative rounded-2xl overflow-hidden aspect-square shadow-inner bg-gray-100 border-2 border-orange-100">
                                <img src={imagePreview} alt="Item" className="w-full h-full object-contain" />
                                <button onClick={resetApp} className="absolute top-4 right-4 bg-white p-3 rounded-full shadow-md text-gray-500 hover:text-red-500">
                                    <Trash2 size={24} />
                                </button>
                            </div>

                            <button
                                onClick={analyzeItem}
                                disabled={loading}
                                className="w-full py-5 rounded-2xl font-bold text-xl text-white shadow-lg transition-all hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-3 bg-gradient-to-r from-orange-400 to-red-500"
                            >
                                {loading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-6 w-6 border-4 border-white border-t-transparent"></div>
                                        {isSpicyMode ? t('cookingMagic') : t('analyzing')}
                                    </>
                                ) : (
                                    <>
                                        <Sparkles size={24} />
                                        {isSpicyMode ? t('generateSpicyListing') : t('generateListing')}
                                    </>
                                )}
                            </button>
                            <p className="text-center text-xs text-gray-400 leading-relaxed px-2">
                                By generating a listing, the final decision on pricing and accuracy is yours alone — we can only help with what we can see.{' '}
                                <a href="https://feelfamous.co.uk/founder" target="_blank" rel="noreferrer" className="text-orange-400 hover:text-orange-300 underline">Become a Founder</a>
                                {' '}for a more in-depth result.
                            </p>
                        </div>
                    )}

                    {/* BULK GRID VIEW */}
                    {showGrid && items.length > 0 && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h2 className="text-xl font-bold text-gray-800">
                                        {items.some(i => i.oracleImport)
                                            ? `🔮 ${items.length} item${items.length > 1 ? 's' : ''} from Oracle`
                                            : `${items.length} items uploaded`
                                        }
                                    </h2>
                                    <p className="text-sm text-gray-500">
                                        {items.filter(i => i.status === 'done').length} done
                                        {items.filter(i => i.status === 'error').length > 0 && (
                                            <span className="text-red-500 ml-2">
                                                {items.filter(i => i.status === 'error').length} failed
                                            </span>
                                        )}
                                    </p>
                                </div>
                                <button onClick={resetApp} className="text-gray-400 hover:text-red-500 p-2">
                                    <X size={24} />
                                </button>
                            </div>

                            {bulkProcessing && (
                                <div className="bg-orange-50 p-4 rounded-2xl border border-orange-200 space-y-3">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-bold text-orange-700">
                                            Processing {processProgress.current} of {processProgress.total}...
                                        </span>
                                        <span className="text-xs text-orange-500">
                                            {Math.round((processProgress.current / processProgress.total) * 100)}%
                                        </span>
                                    </div>
                                    <div className="w-full bg-orange-200 rounded-full h-3">
                                        <div
                                            className="bg-gradient-to-r from-orange-400 to-red-500 h-3 rounded-full transition-all duration-500"
                                            style={{ width: `${(processProgress.current / processProgress.total) * 100}%` }}
                                        ></div>
                                    </div>
                                    {waitingMessage && (
                                        <div className="bg-white p-4 rounded-xl border border-orange-100 text-center transition-all">
                                            <span className="text-2xl block mb-2">{MESSAGE_TYPE_EMOJI[waitingMessage.type] || '✨'}</span>
                                            <p className="text-sm text-gray-700 leading-relaxed font-medium">{waitingMessage.text}</p>
                                            <p className="text-[10px] text-gray-400 mt-2 uppercase tracking-wider">
                                                {waitingMessage.type === 'joke' && 'Dad Joke Break'}
                                                {waitingMessage.type === 'fact' && 'Did You Know?'}
                                                {waitingMessage.type === 'tip' && 'Seller Pro Tip'}
                                                {waitingMessage.type === 'hype' && 'You Got This'}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                                {items.map((item, index) => (
                                    <div
                                        key={item.id}
                                        onClick={() => item.status === 'done' ? viewItemDetail(index) : null}
                                        className={`relative rounded-xl overflow-hidden aspect-square border-2 transition-all ${item.status === 'done'
                                                ? 'border-green-400 cursor-pointer hover:scale-[1.03] hover:shadow-md'
                                                : item.status === 'processing'
                                                    ? 'border-orange-400 animate-pulse'
                                                    : item.status === 'error'
                                                        ? 'border-red-400'
                                                        : 'border-gray-200'
                                            }`}
                                    >
                                        {item.preview ? (
                                            <img src={item.preview} alt={`Item ${index + 1}`} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-purple-900 to-indigo-900 text-white">
                                                <span className="text-3xl mb-1">🔮</span>
                                                <span className="text-xs font-bold">Oracle</span>
                                            </div>
                                        )}

                                        <div className="absolute inset-0 flex items-center justify-center">
                                            {item.status === 'done' && (
                                                <div className="bg-green-500 text-white rounded-full p-1.5 shadow-lg">
                                                    <Check size={16} />
                                                </div>
                                            )}
                                            {item.status === 'processing' && (
                                                <div className="bg-white rounded-full p-2 shadow-lg">
                                                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-orange-500 border-t-transparent"></div>
                                                </div>
                                            )}
                                            {item.status === 'error' && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); retryItem(index); }}
                                                    className="bg-red-500 text-white rounded-full p-1.5 shadow-lg hover:bg-red-600"
                                                >
                                                    <AlertCircle size={16} />
                                                </button>
                                            )}
                                        </div>

                                        {item.status === 'done' && item.results && (
                                            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs font-bold px-2 py-1 text-center">
                                                {userCurrency.symbol}{item.results.priceLow}-{item.results.priceHigh}
                                            </div>
                                        )}

                                        {!bulkProcessing && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); removeItem(index); }}
                                                className="absolute top-1 right-1 bg-black bg-opacity-50 text-white rounded-full p-0.5 hover:bg-opacity-80"
                                            >
                                                <X size={12} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {!bulkProcessing && items.some(i => i.status === 'pending') && (
                                <div className="bg-gray-50 p-4 rounded-2xl border border-gray-200">

                                    {/* OID SPECIALIST MODE PICKER — bulk view — free for everyone */}
                                    <div className="mb-4">
                                        <label className="text-xs font-bold uppercase text-gray-500 tracking-wider block mb-2">Specialist Mode</label>
                                        <div className="flex gap-2 flex-wrap">
                                            {Object.entries(OID_MODES).map(([key, mode]) => (
                                                <button
                                                    key={key}
                                                    type="button"
                                                    onClick={() => setSelectedMode(selectedMode === key ? null : key)}
                                                    className={`px-4 py-2 rounded-xl font-bold text-sm transition-all ${
                                                        selectedMode === key
                                                            ? 'bg-orange-500 text-white shadow-md'
                                                            : 'bg-white border-2 border-gray-200 text-gray-600 hover:border-orange-300'
                                                    }`}
                                                >
                                                    {mode.label}
                                                </button>
                                            ))}
                                        </div>
                                        {selectedMode && (
                                            <p className="text-xs text-orange-600 mt-2 font-medium">
                                                {OID_MODES[selectedMode].name} mode — specialist grading applied to all items
                                            </p>
                                        )}
                                    </div>

                                    <label className="text-xs font-bold uppercase text-gray-500 tracking-wider block mb-2">
                                        {t('extraContext')}
                                    </label>
                                    <textarea
                                        value={bulkContext}
                                        onChange={(e) => setBulkContext(e.target.value)}
                                        placeholder="e.g. These are all books, from a smoke-free home, bought in 2020..."
                                        className="w-full p-3 border border-gray-200 rounded-xl text-sm text-gray-700 resize-none focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-300"
                                        rows={2}
                                    />
                                    <p className="text-xs text-gray-400 mt-1">{t('extraContextHelp')}</p>
                                </div>
                            )}

                            {!bulkProcessing && items.some(i => i.status === 'pending') && (
                                <button
                                    onClick={processAllItems}
                                    className="w-full py-5 rounded-2xl font-bold text-xl text-white shadow-lg transition-all hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-3 bg-gradient-to-r from-orange-400 to-red-500"
                                >
                                    <Sparkles size={24} />
                                    {isSpicyMode
                                        ? t('generateNSpicy')(items.filter(i => i.status === 'pending').length)
                                        : t('generateN')(items.filter(i => i.status === 'pending').length)
                                    }
                                </button>
                            )}

                            {items.some(i => i.status === 'done') && !bulkProcessing && (
                                <div className="space-y-3">
                                    {/* CSV Export - Primary Action */}
                                    <button
                                        onClick={exportCSV}
                                        className="w-full py-4 rounded-2xl font-bold text-lg text-white shadow-lg transition-all hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-3 bg-gradient-to-r from-green-500 to-emerald-600"
                                    >
                                        <Package size={22} />
                                        {t('exportCSVn')(items.filter(i => i.status === 'done').length)}
                                    </button>

                                    {/* Plain text for Vinted, Gumtree, Craigslist etc */}
                                    <button
                                        onClick={() => {
                                            const doneItems = items.filter(i => i.status === 'done' && i.results);
                                            const lines = [];
                                            doneItems.forEach((item, n) => {
                                                const r = item.results;
                                                lines.push('─────────────────────────');
                                                lines.push('ITEM ' + (n + 1) + ' of ' + doneItems.length);
                                                lines.push('─────────────────────────');
                                                lines.push('TITLE: ' + (r.title || ''));
                                                lines.push('PRICE: ' + userCurrency.symbol + (r.priceLow || 0) + ' – ' + userCurrency.symbol + (r.priceHigh || 0));
                                                lines.push('CONDITION: ' + (r.condition || ''));
                                                lines.push('');
                                                lines.push(r.description || '');
                                                lines.push('');
                                            });
                                            const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8;' });
                                            const url = URL.createObjectURL(blob);
                                            const a = document.createElement('a');
                                            a.href = url;
                                            a.download = 'listings-' + new Date().toISOString().split('T')[0] + '.txt';
                                            a.click();
                                            URL.revokeObjectURL(url);
                                        }}
                                        className="w-full py-3 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 bg-purple-50 border-2 border-purple-300 text-purple-700 hover:bg-purple-100 transition-colors"
                                    >
                                        <span>📋</span>
                                        Download Plain Text (Vinted · Gumtree · Craigslist)
                                    </button>

                                    {/* Upload to eBay Seller Hub */}
                                    <a
                                        href="https://www.ebay.co.uk/sh/reports/uploads"
                                        target="_blank"
                                        rel="noreferrer"
                                        className="w-full py-3 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 bg-red-50 border-2 border-red-300 text-red-700 hover:bg-red-100 transition-colors"
                                    >
                                        <span>🏪</span>
                                        Upload CSV to eBay Seller Hub →
                                    </a>

                                    {/* Bulk save to Hamlet stall — Founders only */}
                                    {isPro && patreonTier === 'founder' && hamletSlug ? (
                                        <button
                                            onClick={saveAllToHamlet}
                                            disabled={bulkHamletStatus?.saving}
                                            className="w-full py-3 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 bg-orange-50 border-2 border-orange-300 text-orange-700 hover:bg-orange-100 transition-colors disabled:opacity-50"
                                        >
                                            <span>🏛️</span>
                                            {bulkHamletStatus?.saving
                                                ? `Saving ${bulkHamletStatus.done}/${bulkHamletStatus.total} to your Hamlet…`
                                                : bulkHamletStatus
                                                    ? `✓ Saved ${bulkHamletStatus.done - bulkHamletStatus.failed}/${bulkHamletStatus.total}${bulkHamletStatus.failed ? ` (${bulkHamletStatus.failed} failed)` : ''}`
                                                    : `Save all ${items.filter(i => i.status === 'done').length} to my Hamlet`
                                            }
                                        </button>
                                    ) : (
                                        <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-2xl px-4 py-3">
                                            <div>
                                                <span className="text-xs font-bold uppercase text-gray-400 tracking-wider">🏛️ Save all to your Hamlet stall</span>
                                                <p className="text-xs text-gray-400 mt-0.5">A hosted, public page for your stall — one of the things Founder support keeps running</p>
                                            </div>
                                            <a href={STRIPE_FOUNDER_LINK} className="text-xs font-bold text-orange-600 hover:text-orange-700 whitespace-nowrap ml-3">Learn more →</a>
                                        </div>
                                    )}

                                    {/* Quick Bulk Actions */}
                                    <div className="bg-gray-50 p-4 rounded-2xl border border-gray-200 space-y-3">
                                        <p className="text-xs font-bold uppercase text-gray-500 tracking-wider">Bulk Actions</p>
                                        <div className="flex gap-2 flex-wrap">
                                            <button onClick={() => applyBulkCondition('Excellent condition')} className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-100">
                                                All "Excellent"
                                            </button>
                                            <button onClick={() => applyBulkCondition('Very good condition')} className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-100">
                                                All "Very Good"
                                            </button>
                                            <button onClick={() => applyBulkCondition('Good condition')} className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-100">
                                                All "Good"
                                            </button>
                                        </div>
                                        <div className="flex gap-2">
                                            <button onClick={() => applyPriceAdjust(10)} className="flex-1 bg-green-50 border border-green-200 text-green-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-green-100">
                                                Prices +10%
                                            </button>
                                            <button onClick={() => applyPriceAdjust(-10)} className="flex-1 bg-red-50 border border-red-200 text-red-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-red-100">
                                                Prices -10%
                                            </button>
                                        </div>
                                        <div className="flex gap-2">
                                            <button onClick={() => applyBulkNote('From a smoke-free, pet-free home.')} className="flex-1 bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-100">
                                                + Smoke-free home
                                            </button>
                                            <button onClick={() => applyBulkNote('Happy to combine postage on multiple items.')} className="flex-1 bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-100">
                                                + Combine postage
                                            </button>
                                        </div>
                                    </div>

                                    {/* Summary */}
                                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-2xl border-2 border-green-200 text-center">
                                        <p className="text-sm text-green-700 mb-1">{t('totalEstValue')}</p>
                                        <p className="text-3xl font-black text-green-600">
                                            {userCurrency.symbol}
                                            {items.filter(i => i.results).reduce((sum, i) => sum + (i.results.priceLow || 0), 0).toFixed(0)}
                                            {' - '}
                                            {items.filter(i => i.results).reduce((sum, i) => sum + (i.results.priceHigh || 0), 0).toFixed(0)}
                                        </p>
                                        <p className="text-xs text-green-600 mt-2">
                                            Time saved: ~{items.filter(i => i.results).length * SAVINGS_PER_LISTING.time} minutes
                                        </p>
                                    </div>
                                </div>
                            )}

                            {!bulkProcessing && items.length > 0 && items.every(i => i.status === 'done' || i.status === 'error') && items.some(i => i.status === 'done') && (
                                <div className="text-center p-4">
                                    <p className="text-sm text-gray-500">
                                        Click any item to view/edit details. Download CSV → upload to eBay Seller Hub → add photos to each draft → publish.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {results && !showGrid && (
                        <div className="space-y-6">

                            {items.length > 0 && (
                                <button onClick={backToGrid} className="flex items-center gap-2 text-gray-500 hover:text-gray-700 font-medium text-sm mb-2">
                                    <span>&larr;</span> Back to all {items.length} items
                                </button>
                            )}

                            {items.some(i => i.oracleImport) && !image && (
                                <div className="bg-gradient-to-r from-purple-100 to-indigo-100 border border-purple-200 rounded-2xl p-4 text-center">
                                    <p className="text-sm font-bold text-purple-800">🔮 Imported from Magic-Oid Oracle</p>
                                    <p className="text-xs text-purple-600 mt-1">Add a photo to include it in your eBay listing</p>
                                </div>
                            )}

                            <div ref={resultCardRef} className="bg-white p-2 rounded-xl">
                                {results._securityWarning && (
                                    <div className="mb-4 p-3 rounded-xl border border-amber-300 bg-amber-50 text-amber-800 text-sm font-medium">
                                        ⚠️ {results._securityWarning}
                                    </div>
                                )}
                                {isSpicyMode && (
                                    <div className={`mb-6 p-4 rounded-2xl border-2 ${getRarityStyle(results.rarity).bg} ${getRarityStyle(results.rarity).color} text-center`}>
                                        <p className="text-lg font-bold italic text-gray-800">"{results.spicyComment}"</p>
                                        <span className="text-xs uppercase font-black tracking-widest text-gray-500 mt-2 block">
                                            {results.rarity} TIER FIND {getRarityStyle(results.rarity).emoji}
                                        </span>
                                    </div>
                                )}

                                <div className="bg-orange-50 p-5 rounded-2xl border border-orange-100 mb-4">
                                    <div className="flex justify-between items-start mb-2">
                                        <span className="text-xs font-bold uppercase text-orange-600 tracking-wider">Listing Title</span>
                                        <button onClick={() => copyText('title', results.title)} className="text-orange-500 hover:text-orange-700">
                                            {copiedSection === 'title' ? <Check size={18} /> : <Copy size={18} />}
                                        </button>
                                    </div>
                                    <h2 className="text-xl font-bold text-gray-900 leading-tight">{results.title}</h2>
                                </div>

                                <div className="bg-gray-50 p-5 rounded-2xl border border-gray-200 mb-4">
                                    <div className="flex justify-between items-start mb-2">
                                        <span className="text-xs font-bold uppercase text-gray-500 tracking-wider">Description</span>
                                        <button onClick={() => copyText('desc', results.description)} className="text-gray-400 hover:text-gray-600">
                                            {copiedSection === 'desc' ? <Check size={18} /> : <Copy size={18} />}
                                        </button>
                                    </div>
                                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{results.description}</p>
                                </div>

                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div className="bg-green-50 p-4 rounded-2xl border border-green-100 text-center">
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="text-xs font-bold uppercase text-green-600">Est. Value</span>
                                            <button onClick={() => setEditingPrice(!editingPrice)} className="text-green-500 hover:text-green-700" title="Edit price">
                                                <Edit3 size={16} />
                                            </button>
                                        </div>
                                        {!editingPrice ? (
                                            <p className="text-2xl font-black text-green-700">{userCurrency.symbol}{editedPrice?.priceLow ?? results.priceLow} - {editedPrice?.priceHigh ?? results.priceHigh}</p>
                                        ) : (
                                            <div className="flex items-center gap-1 justify-center mt-1">
                                                <span className="text-green-700 font-bold">{userCurrency.symbol}</span>
                                                <input
                                                    type="number" step="0.01" min="0"
                                                    value={editedPrice?.priceLow ?? results.priceLow}
                                                    onChange={e => setEditedPrice(p => ({ priceHigh: p?.priceHigh ?? results.priceHigh, priceLow: parseFloat(e.target.value) || 0 }))}
                                                    className="w-16 border border-green-300 rounded px-1 py-0.5 text-center text-sm font-bold text-green-800 bg-white"
                                                />
                                                <span className="text-green-600">–</span>
                                                <span className="text-green-700 font-bold">{userCurrency.symbol}</span>
                                                <input
                                                    type="number" step="0.01" min="0"
                                                    value={editedPrice?.priceHigh ?? results.priceHigh}
                                                    onChange={e => setEditedPrice(p => ({ priceLow: p?.priceLow ?? results.priceLow, priceHigh: parseFloat(e.target.value) || 0 }))}
                                                    className="w-16 border border-green-300 rounded px-1 py-0.5 text-center text-sm font-bold text-green-800 bg-white"
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="bg-blue-50 p-4 rounded-2xl border border-blue-100 text-center">
                                        <span className="text-xs font-bold uppercase text-blue-600">Category</span>
                                        <p className="text-sm font-bold text-blue-800 mt-2 leading-tight">{results.category}</p>
                                    </div>
                                </div>

                                <div className="bg-blue-50 p-5 rounded-2xl border border-blue-100 mb-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <Ruler className="text-blue-600" size={20} />
                                            <span className="text-xs font-bold uppercase text-blue-600">Dimensions & Weight</span>
                                        </div>
                                        <button onClick={() => setEditingDimensions(!editingDimensions)} className="text-blue-500 hover:text-blue-700">
                                            <Edit3 size={18} />
                                        </button>
                                    </div>

                                    {!editingDimensions ? (
                                        <>
                                            <div className="flex items-center gap-4 mb-2">
                                                <div className="flex-1">
                                                    <p className="text-lg font-bold text-blue-900">
                                                        📏 {editedDimensions?.length || results.dimensions.length} × {editedDimensions?.width || results.dimensions.width} × {editedDimensions?.height || results.dimensions.height} cm
                                                    </p>
                                                    <p className="text-sm text-blue-600 mt-1">
                                                        <Scale size={14} className="inline mr-1" />
                                                        Weight: ~{results.weight.grams}g
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex gap-2 text-xs text-blue-500 mt-2">
                                                <span>Confidence: {editedDimensions?.confidence || results.dimensions.confidence}%</span>
                                                <span>•</span>
                                                <span className="capitalize">{results.material}</span>
                                                <span>•</span>
                                                <span className="capitalize">{results.fragility} fragility</span>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="space-y-3">
                                            <div className="grid grid-cols-3 gap-2">
                                                <div>
                                                    <label className="text-xs text-blue-600 block mb-1">Length (cm)</label>
                                                    <input
                                                        type="number"
                                                        value={editedDimensions?.length || ''}
                                                        onChange={(e) => handleDimensionChange('length', e.target.value)}
                                                        className="w-full p-2 border border-blue-200 rounded-lg text-center font-bold"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-xs text-blue-600 block mb-1">Width (cm)</label>
                                                    <input
                                                        type="number"
                                                        value={editedDimensions?.width || ''}
                                                        onChange={(e) => handleDimensionChange('width', e.target.value)}
                                                        className="w-full p-2 border border-blue-200 rounded-lg text-center font-bold"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-xs text-blue-600 block mb-1">Height (cm)</label>
                                                    <input
                                                        type="number"
                                                        value={editedDimensions?.height || ''}
                                                        onChange={(e) => handleDimensionChange('height', e.target.value)}
                                                        className="w-full p-2 border border-blue-200 rounded-lg text-center font-bold"
                                                    />
                                                </div>
                                            </div>
                                            {dimensionWarning && (
                                                <div className="flex items-center gap-2 text-amber-600 text-sm bg-amber-50 p-2 rounded-lg">
                                                    <AlertCircle size={16} />
                                                    {dimensionWarning}
                                                </div>
                                            )}
                                            <button onClick={saveDimensionEdits} className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-lg font-bold text-sm">
                                                Save Dimensions
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {packaging && !hasOwnPackaging && (
                                    <div className="bg-orange-50 p-5 rounded-2xl border border-orange-200 mb-4">
                                        <div className="flex items-center gap-2 mb-3">
                                            <Package className="text-orange-600" size={20} />
                                            <span className="text-xs font-bold uppercase text-orange-600">Recommended Packaging</span>
                                        </div>
                                        <div className="bg-white p-4 rounded-xl border-2 border-orange-300 mb-3">
                                            <div className="flex items-center gap-3">
                                                <span className="text-3xl">{packaging.details.icon}</span>
                                                <div className="flex-1">
                                                    <p className="font-bold text-gray-900">{packaging.details.name}</p>
                                                    <p className="text-sm text-gray-600">{packaging.details.description}</p>
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        {packaging.details.dimensions}, up to {packaging.details.maxWeight >= 1000 ? `${packaging.details.maxWeight / 1000}kg` : `${packaging.details.maxWeight}g`}
                                                    </p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-2xl font-black text-orange-600">£{packaging.details.price.toFixed(2)}</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <button className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-2 rounded-lg font-bold text-sm transition-colors">
                                                Order This Size
                                            </button>
                                            <button onClick={() => setHasOwnPackaging(true)} className="flex-1 bg-white border-2 border-orange-200 text-orange-600 py-2 rounded-lg font-bold text-sm hover:bg-orange-50 transition-colors">
                                                I Have It
                                            </button>
                                        </div>
                                        {packaging.alternatives && packaging.alternatives.length > 0 && (
                                            <p className="text-xs text-gray-500 mt-3 text-center">
                                                Alternative: {packaging.alternatives[0].name} (£{packaging.alternatives[0].price.toFixed(2)})
                                            </p>
                                        )}
                                        <p className="text-xs text-orange-600 mt-2 text-center italic">{packaging.reasoning}</p>
                                    </div>
                                )}

                                {hasOwnPackaging && (
                                    <div className="bg-green-50 p-4 rounded-2xl border border-green-200 mb-4 text-center">
                                        <p className="text-green-700 font-bold flex items-center justify-center gap-2">
                                            <Check size={20} /> Using your own packaging
                                        </p>
                                        <button onClick={() => setHasOwnPackaging(false)} className="text-green-600 text-sm underline mt-1">
                                            Show recommendations again
                                        </button>
                                    </div>
                                )}
                            </div>

                            {(() => {
                                const oid = getOidRecommendation(results);
                                return oid ? (
                                    <a
                                        href={oid.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="flex items-center gap-3 bg-indigo-50 border-2 border-indigo-200 rounded-2xl p-4 mb-3 hover:border-indigo-400 hover:bg-indigo-100 transition-colors"
                                    >
                                        <span className="text-3xl">{oid.emoji}</span>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-black text-indigo-900 text-sm leading-tight">Know exactly what you've got?</p>
                                            <p className="text-indigo-700 text-sm font-medium">{oid.name} — {oid.tagline} →</p>
                                        </div>
                                    </a>
                                ) : null;
                            })()}

                            {/* CREDITS CTA */}
                            {/* Lester (auto-list to eBay) coming soon — credits held, not shown yet */}

                            <div className="space-y-3">
                                <a
                                    href="https://feelfamous.co.uk"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-all transform hover:scale-[1.02] active:scale-95 shadow-lg"
                                >
                                    <ExternalLink size={20} />
                                    Join the FeelFamous Family 💚
                                </a>

                                <div className="grid grid-cols-4 gap-2">
                                    <button
                                        onClick={() => {
                                            const searchQuery = encodeURIComponent(results.title);
                                            window.open(`https://www.ebay.co.uk/sch/i.html?_nkw=${searchQuery}&LH_Sold=1&LH_Complete=1`, '_blank');
                                        }}
                                        className="bg-[#e53238] hover:bg-[#d12d33] text-white py-3 rounded-xl font-bold flex flex-col items-center justify-center gap-1 text-sm transition-colors"
                                    >
                                        <span className="text-lg">🛒</span>
                                        <span className="text-xs">Sold</span>
                                    </button>
                                    <button onClick={copyForStore} className="bg-blue-500 hover:bg-blue-600 text-white py-3 rounded-xl font-bold flex flex-col items-center justify-center gap-1 text-sm transition-colors">
                                        {copiedStore ? <Check size={18} /> : <Copy size={18} />}
                                        <span className="text-xs">{copiedStore ? 'Copied!' : 'Copy'}</span>
                                    </button>
                                    <button onClick={shareResult} className="bg-gray-900 hover:bg-gray-800 text-white py-3 rounded-xl font-bold flex flex-col items-center justify-center gap-1 text-sm transition-colors">
                                        <Share2 size={18} />
                                        <span className="text-xs">Share</span>
                                    </button>
                                    <button onClick={resetApp} className="bg-white border-2 border-gray-200 text-gray-600 py-3 rounded-xl font-bold hover:bg-gray-50 flex flex-col items-center justify-center gap-1 text-sm transition-colors">
                                        <Trash2 size={18} />
                                        <span className="text-xs">New</span>
                                    </button>
                                </div>
                            </div>

                            <a
                                href="https://spicylister.co.uk"
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-start gap-3 bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-amber-200 rounded-2xl p-4 mt-3 hover:border-amber-400 transition-colors"
                            >
                                <span className="text-3xl">🏘️</span>
                                <div>
                                    <p className="font-black text-amber-900 text-sm">Sell it in real life too</p>
                                    <p className="text-amber-800 text-sm mt-1 leading-snug">The Womble Village — a community marketplace for kind buyers and sellers. Find your nearest boot sale, see where your favourite sellers have their stall, and let people know when you're out selling.</p>
                                    <p className="text-amber-600 text-xs font-bold mt-2">spicylister.co.uk →</p>
                                </div>
                            </a>

                            {/* FREE: Shop Front generator — paste into eBay's HTML editor */}
                            {results && (
                                <div className="bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-200 rounded-2xl p-4 mt-3">
                                    <p className="font-black text-orange-900 text-sm mb-1">🏪 Add your item to your stall — free</p>
                                    <p className="text-orange-800 text-xs leading-relaxed mb-3">
                                        A bit like Garage Sale for Mac, but free. Copy the HTML below and paste it into eBay's listing description (HTML editor) — it turns this listing into a branded shop front, made with love by SpicyLister.
                                    </p>
                                    <img
                                        src="/images/shop-front-how-it-works.jpg"
                                        alt="How it works: photograph your item, SpicyLister's AI writes the listing, copy-paste into eBay, get paid and post it"
                                        className="w-full rounded-xl mb-3"
                                        loading="lazy"
                                    />
                                    <button
                                        onClick={copyShopFrontHtml}
                                        className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm px-4 py-2 rounded-xl flex items-center justify-center gap-2"
                                    >
                                        {copiedSection === 'shopfront' ? <><Check size={16} /> Copied — paste into eBay's HTML editor</> : <><Copy size={16} /> Copy my shop front HTML</>}
                                    </button>
                                    <p className="text-orange-700 text-[11px] mt-2 text-center">
                                        Want a live shop window that updates itself, no copy-paste needed? Founders (£14.95/mo) get their own hosted stall — see below.
                                    </p>
                                </div>
                            )}

                            {/* FOUNDER: Save to Hamlet */}
                            {isPro && patreonTier === 'founder' && results && (
                                <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-2xl p-4 mt-3">
                                    <p className="font-black text-orange-900 text-sm mb-1">🏛️ Save to your Hamlet</p>
                                    {!showHamletSetup && hamletSlug && (
                                        <div className="mt-2">
                                            <input
                                                type="url"
                                                placeholder="eBay listing URL (optional — shows Buy on eBay button)"
                                                value={hamletEbayUrl}
                                                onChange={e => setHamletEbayUrl(e.target.value)}
                                                className="w-full border-2 border-orange-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-orange-400 mb-2"
                                                aria-label="eBay listing URL"
                                            />
                                            <div className="flex items-center gap-3">
                                                <button
                                                    onClick={() => saveToHamlet(hamletSlug)}
                                                    disabled={hamletSaveStatus === 'saving'}
                                                    className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold text-sm px-4 py-2 rounded-xl"
                                                >
                                                    {hamletSaveStatus === 'saving' ? 'Saving…' : hamletSaveStatus === 'saved' ? '✓ Saved!' : hamletSaveStatus === 'error' ? 'Error — try again' : 'Save listing'}
                                                </button>
                                                <a href={`/hamlet/${hamletSlug}`} target="_blank" rel="noopener noreferrer" className="text-orange-600 text-xs underline">View hamlet →</a>
                                                <button onClick={() => setShowHamletSetup(true)} className="text-orange-400 text-xs underline ml-auto">Change slug</button>
                                            </div>
                                        </div>
                                    )}
                                    {(!hamletSlug || showHamletSetup) && (
                                        <div className="mt-2">
                                            <p className="text-orange-800 text-xs leading-relaxed mb-3">
                                                Your listing (title, description, tags, price) is saved to Supabase and shown on your public hamlet page at <strong>spicylister.com/hamlet/your-slug</strong>. Buyers pay you directly via PayPal — SpicyLister takes nothing. You can delete all your data instantly. <a href="https://feelfamous.co.uk/privacy.html" className="underline">Privacy policy</a>
                                            </p>
                                            <div className="flex flex-col gap-2">
                                                <input
                                                    type="text"
                                                    placeholder="hamlet-slug (e.g. doc-strange)"
                                                    value={hamletSlugInput}
                                                    onChange={e => setHamletSlugInput(e.target.value)}
                                                    className="border-2 border-orange-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-orange-400"
                                                    aria-label="Your hamlet slug"
                                                />
                                                <input
                                                    type="text"
                                                    placeholder="PayPal.me username (optional)"
                                                    value={hamletPaypal}
                                                    onChange={e => setHamletPaypal(e.target.value)}
                                                    className="border-2 border-orange-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-orange-400"
                                                    aria-label="PayPal.me username"
                                                />
                                                <input
                                                    type="tel"
                                                    placeholder="WhatsApp/Signal number e.g. 447700900000 (optional)"
                                                    value={hamletWhatsapp}
                                                    onChange={e => setHamletWhatsapp(e.target.value)}
                                                    className="border-2 border-orange-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-orange-400"
                                                    aria-label="Contact number for enquiries (WhatsApp/Signal)"
                                                />
                                                <input
                                                    type="url"
                                                    placeholder="eBay listing URL (optional)"
                                                    value={hamletEbayUrl}
                                                    onChange={e => setHamletEbayUrl(e.target.value)}
                                                    className="border-2 border-orange-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-orange-400"
                                                    aria-label="eBay listing URL"
                                                />
                                                <button
                                                    onClick={() => confirmHamletSlug(hamletSlugInput, hamletPaypal, hamletWhatsapp)}
                                                    className="bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm px-4 py-2 rounded-xl"
                                                >
                                                    Save listing to hamlet
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* FOUNDER: Germany Gift Codes — real Patreon founder session, verified server-side by gift-generate.js */}
                            {isPro && patreonTier === 'founder' && (
                                <GiftCodeGenerator />
                            )}

                            {currentSavings && (
                                <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-3xl border-2 border-green-200 text-center">
                                    <h2 className="text-3xl font-black text-green-700 mb-3">
                                        {milestone?.show ? milestone.message : '🎉 Well Done!'}
                                    </h2>
                                    <div className="bg-white p-4 rounded-2xl mb-4">
                                        <p className="text-gray-700 mb-2">You've saved:</p>
                                        <p className="text-3xl font-black text-green-600">
                                            {SAVINGS_PER_LISTING.time} min & £{SAVINGS_PER_LISTING.cost.toFixed(2)}
                                        </p>
                                    </div>
                                    {currentReward && (
                                        <div className="bg-green-100 p-4 rounded-2xl mb-4">
                                            <p className="text-sm text-green-800 mb-2 font-semibold">You've earned time for:</p>
                                            <p className="text-lg font-bold text-green-700">{currentReward}</p>
                                        </div>
                                    )}
                                    <p className="text-xs text-green-600">
                                        💚 Total saved: {formatTime(currentSavings.time)}, £{currentSavings.cost.toFixed(2)} across {currentSavings.count} listings
                                    </p>
                                </div>
                            )}

                            <div className="bg-orange-50 p-4 rounded-2xl border border-orange-100 text-center">
                                <p className="text-sm text-gray-600 leading-relaxed">
                                    This is free, always will be — that's not changing. It saved you time just now, and it costs real money to keep running.{' '}
                                    Tell a mate who'd use it too, or pay it forward some other way. That's genuinely enough.
                                </p>
                                {!isPro && (
                                    <p className="text-sm text-gray-500 mt-2">
                                        If you'd rather say thanks with money: a one-off{' '}
                                        <a href={HONESTY_BOX_URL} target="_blank" rel="noreferrer" className="text-orange-600 font-bold hover:text-orange-700 underline">buy me a coffee</a>
                                        {' '}covers it, or{' '}
                                        <a href={STRIPE_VILLAGER_LINK} className="text-orange-600 font-bold hover:text-orange-700 underline">become a Villager</a>
                                        {' '}(99p first month) if you want to be a regular. Either way, nothing here was ever locked behind it.
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* FOOTER */}
                <div className="mt-12 text-center space-y-6 pb-12">

                    <div className="inline-block bg-black px-5 py-3 rounded-lg border-2 border-green-500 shadow-lg shadow-green-500/20">
                        <p className="text-[10px] text-green-400 uppercase tracking-widest mb-1">🌍 Global Listings Generated</p>
                        <div className="font-mono text-3xl text-green-400 tracking-wider" style={{ textShadow: '0 0 10px #22c55e' }}>
                            {globalCount != null ? String(globalCount).padStart(6, '0') : '------'}
                        </div>
                        <p className="text-[10px] text-green-600 mt-2">
                            Your contribution: <span className="text-green-400 font-bold">{listingCount}</span>
                        </p>
                        <p className="text-[8px] text-green-700 mt-1 opacity-70">~ GlowGadgets Vibes ~</p>
                    </div>

                    <div className="bg-gradient-to-r from-purple-100 via-pink-100 to-orange-100 p-4 rounded-2xl border-2 border-purple-200">
                        <p className="text-lg font-black text-gray-800 mb-1">
                            Made with 🧠✨ by <span className="text-purple-600">AuDHD</span> Chris P Tee
                        </p>
                    </div>

                    <div className="bg-white px-6 py-5 rounded-3xl shadow-md border-2 border-orange-200">
                        <h3 className="text-xl font-black text-gray-800 text-center mb-2">
                            🏡 Join the village
                        </h3>
                        <p className="text-sm text-gray-600 text-center mb-1">It's free to use. Always.</p>
                        <p className="text-sm text-gray-700 text-center mb-4">99p proves you're a real person, not a bot — then £4.95/month makes you a Villager, <strong>which keeps the bots out for good.</strong> No adverts thrown at you. No algorithm deciding what you see. No being forced to listen to somebody else's ramblings. You put out what you want other people to learn from your special interests — and that's exactly what you'll find when you visit other villagers too. It's not about criticising or judging each other's ability. It's about respecting that we're all willing to give what we know, because it could be the missing chapter — or the missing puzzle piece — in somebody else's life.</p>
                        <a
                            href={STRIPE_VILLAGER_LINK}
                            className="block w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-5 rounded-xl transition-colors text-center mb-2"
                        >
                            Come in — 99p to start
                        </a>
                        <p className="text-[11px] text-gray-400 text-center mb-2">Cancel any time. SpicyLister takes nothing from your sales.</p>
                        <a
                            href={PATREON_OAUTH_URL}
                            className="block text-xs text-gray-400 hover:text-gray-600 underline text-center"
                            onClick={persistPendingResults}
                        >
                            Already support on Patreon? Sign in
                        </a>
                    </div>

                    <div className="bg-white px-6 py-5 rounded-3xl shadow-md border-2 border-gray-200">
                        <h3 className="text-lg font-black text-gray-800 mb-4">📚 Chris P Tee Recommends</h3>

                        <div className="bg-yellow-50 p-4 rounded-2xl border-2 border-yellow-300 mb-4">
                            <p className="text-xs font-bold text-yellow-700 uppercase tracking-wider mb-2">✨ Books by Doc Strange & Chris P Tee</p>
                            <div className="space-y-3 text-sm">
                                <a href="https://amzn.to/4vR91yh" target="_blank" rel="noreferrer" className="block bg-amber-50 border-2 border-amber-400 rounded-xl p-3 text-gray-700 hover:bg-amber-100 transition-colors">
                                    <span className="block font-bold text-amber-900">🆕 The Honest Hypnotist Guide — Unmasking the AuDHD Way</span>
                                    <span className="block text-xs text-amber-700 mt-1">By Doc Strange (Chris P Tee). Brand new.</span>
                                </a>
                                <a href="https://amzn.to/4unS1hH" target="_blank" rel="noreferrer" className="block bg-indigo-50 border border-indigo-200 rounded-xl p-3 text-gray-700 hover:bg-indigo-100 transition-colors">
                                    <span className="block font-bold text-indigo-800">🌀 A Strange Way to Stage Hypnosis</span>
                                    <span className="block text-xs text-indigo-700 mt-1">By Doc Strange. The real secrets of stage hypnosis.</span>
                                </a>
                                <a href="https://glowgadgeteer.gumroad.com/l/uk-medical-cannabis-patient-guide" target="_blank" rel="noreferrer" className="block bg-green-50 border border-green-200 rounded-xl p-3 text-gray-700 hover:bg-green-100 transition-colors">
                                    <span className="block font-bold text-green-800">📖 Your First Cannabis Prescription — £9</span>
                                    <span className="block text-xs text-green-700 mt-1">What nobody tells you. UK patient guide by Doc Strange.</span>
                                </a>
                                <a href="https://glowgadgeteer.gumroad.com/l/kids-magic-party-guide" target="_blank" rel="noreferrer" className="block bg-purple-50 border border-purple-200 rounded-xl p-3 text-gray-700 hover:bg-purple-100 transition-colors">
                                    <span className="block font-bold text-purple-800">🪄 Stress-Free Kids Magic Parties — £5</span>
                                    <span className="block text-xs text-purple-700 mt-1">Magic Circle pro. 25 years. Everything you need to know.</span>
                                </a>
                            </div>
                        </div>

                        <div className="bg-orange-50 p-4 rounded-2xl border-2 border-orange-300 mb-4">
                            <p className="text-xs font-bold text-orange-700 uppercase tracking-wider mb-2">☕ My Coffee Gear (10+ Years Use)</p>
                            <p className="text-xs text-orange-600 mb-2 italic">"ADHD + Coffee = Functional Human"</p>
                            <div className="space-y-2 text-sm">
                                <a href="https://amzn.to/3LLjx8x" target="_blank" rel="noreferrer" className="block text-gray-700 hover:text-orange-600">⏰ Bedside Alarm Clock Coffee Maker (on my bedside NOW)</a>
                                <a href="https://amzn.to/4kcn4cr" target="_blank" rel="noreferrer" className="block text-gray-700 hover:text-orange-600">🏆 Aeropress Classic (10 years old, good as new)</a>
                                <a href="https://amzn.to/45GzK5B" target="_blank" rel="noreferrer" className="block text-gray-700 hover:text-orange-600">♻️ Reusable Metal Filter (no more paper!)</a>
                                <a href="https://amzn.to/4bvlRee" target="_blank" rel="noreferrer" className="block text-gray-700 hover:text-orange-600">🇮🇹 Bialetti Moka Pot (bulletproof Italian coffee)</a>
                            </div>
                        </div>

                        <div className="bg-purple-50 p-4 rounded-2xl border-2 border-purple-300">
                            <p className="text-xs font-bold text-purple-700 uppercase tracking-wider mb-2">🎵 From a TikTok Friend</p>
                            <a href="https://amzn.to/4q6XLd9" target="_blank" rel="noreferrer" className="block text-sm text-gray-700 hover:text-purple-600">
                                📖 Rave & Glowgadgets: Bristol 1999-2003 - We were BOTH there!
                            </a>
                        </div>

                        <p className="text-xs text-gray-400 mt-3 text-center">Affiliate links support SpicyLister 💚</p>
                    </div>

                    <div className="flex flex-col items-center gap-2 text-sm text-gray-600">
                        <a href="https://comedymagic.co.uk" target="_blank" rel="noreferrer" className="font-bold text-purple-700 hover:text-purple-800 transition-colors">
                            Support the <strong>Community Comedy Magic Tour</strong>
                        </a>
                        <p className="text-xs text-gray-500 mt-4">
                            SpicyLister v2.4 • Powered by Gemini Flash • No cookies, just vibes 🌶️
                        </p>
                        <p className="text-[10px] text-gray-500">
                            🔒 Anonymous &amp; no tracking — we never see what you're selling
                        </p>
                        <p className="text-[10px] text-gray-400">
                            "Just trying to be useful. One ember at a time." 💚
                        </p>
                    </div>
                </div>

            </div>
        </div>
    );
}
